/*
  CODEX CUSTOMIZATION START
  File: custom-base.js
  Purpose:
  - Keep custom JavaScript separate from Impact core files.
  - Recalculate section spacing when Codex-hidden sections are skipped.
  - Apply Custom HTML – Extended mobile-only classes under 700px.
  - Improve large_1col mobile pill navigation (swipe + optional arrows).
  - Add defensive close handlers for the OOL desktop mega menu.
  - Guarantee that the global bypass controls transfer focus or open the accessibility widget,
    including primary-H1 focus for every non-homepage Skip to content destination.
  - Make the header logo, product-title, and main-navigation links operable with an unmodified Space key,
    while preserving native link semantics and leaving unrelated links unchanged.
  - Show Google Automated Discounts on collection/search cards only for matching discount sessions.
  - Canonicalize selected full-page search terms without changing predictive search.
  - Preserve mapped-search display text and focus through AJAX facet updates.
  CODEX CUSTOMIZATION END
*/

(() => {
  const MAIN_SELECTOR = '[role="main"]';
  const SKIP_TO_CONTENT_SELECTOR = '.skip-to-content[href="#main"]';
  const MAIN_HEADING_SELECTOR = 'h1';
  const HOME_MAIN_HEADING_SELECTOR = 'h2';
  const HOME_PAGE_TYPE = 'index';
  const FOOTER_SELECTOR = 'footer#footer';
  const SKIP_TO_FOOTER_SELECTOR = '.skip-to-footer[href="#footer"], [data-codex-skip-to-footer], #csm-ada-skip-to-footer, .csm-ada-skip-button';
  const HEADER_LOGO_LINK_SELECTOR = 'a.header__logo[href], .header__logo > a[href]';
  const PRODUCT_TITLE_LINK_SELECTOR = '.product-card__title a[href]';
  const NAVIGATION_DROPDOWN_SUMMARY_SELECTOR = 'details[is="dropdown-disclosure"] > summary, details[is="mega-menu-disclosure"] > summary';
  const NAVIGATION_DROPDOWN_LINK_SELECTOR = 'details[is="dropdown-disclosure"] .dropdown-menu a[href], details[is="mega-menu-disclosure"] a[href]';
  const OPEN_ACCESSIBILITY_WIDGET_SELECTOR = '[data-codex-open-accessibility-widget]';
  const CANONICAL_BYPASS_SELECTOR = '#codex-bypass-links';
  const CONSENTMO_BYPASS_SELECTOR = [
    '.csm-ada-skip-buttons',
    '#csm-ada-skip-to-main',
    '#csm-ada-skip-to-footer',
    '#csm-ada-skip-to-widget',
  ].join(', ');
  const CONSENTMO_SKIP_TO_WIDGET_SELECTOR = '#csm-ada-skip-to-widget';
  const CONSENTMO_FOOTER_COOKIE_ITEM_SELECTOR = '.footer__block--menu li.csm-cookie-widget-link[data-csm-cookie-widget-wrapper]';
  const CONSENTMO_FOOTER_COOKIE_BUTTON_ATTRIBUTE = 'data-codex-consentmo-cookie-button';
  const CONSENTMO_WIDGET_TRIGGER_SELECTOR = [
    '.csm-accessibility-link',
    '#csm-ada-skip-to-widget',
    'button',
    'a',
    '[role="button"]',
  ].join(', ');
  const CONSENTMO_ACCESSIBILITY_CLOSED_EVENT = 'consentmo:accessibilityClosed';
  const CONSENTMO_ACCESSIBILITY_FOCUS_DELAY_MS = 400;
  const HIDDEN_SECTION_SELECTOR = '[data-codex-hidden-section]';
  const MOBILE_ONLY_CLASS_SELECTOR = '[data-codex-mobile-class]';
  const MOBILE_CLASS_BASE_ATTRIBUTE = 'data-codex-mobile-class-base';
  const PRODUCT_PILLS_SELECTOR = '.shopify-section--custom-html-extended :is(.standard_2col, .pill_2col, .large_1col) .product-properties__pills';
  const PRODUCT_AWARDS_SELECTOR = '.shopify-section--custom-html-extended .awards_2col .awards-section';
  const LARGE_ONE_COL_PILLS_SELECTOR = '.shopify-section--custom-html-extended .large_1col .product-properties__pills';
  const OOL_MEGA_MENU_SELECTOR = '.ool-mega-menu';
  const OOL_MEGA_MENU_PANEL_SELECTOR = '.ool-mega-menu__panel';
  const CONSENTMO_BANNER_ACTION_PATTERNS = [
    /^preferences$/i,
    /^reject$/i,
    /^accept$/i,
  ];
  const CONSENTMO_ACCESSIBILITY_PATTERNS = [
    /accessibility menu/i,
    /open widget/i,
    /pause animations/i,
  ];
  const GOOGLE_CARD_PRICE_LIST_SELECTOR = [
    '.shopify-section--main-collection product-card [data-codex-google-card-price-list]',
    '.shopify-section--main-search product-card [data-codex-google-card-price-list]',
    '.predictive-search-result [data-codex-google-card-price-list]',
  ].join(', ');
  const GOOGLE_CARD_PRICE_ROW_ATTRIBUTE = 'data-codex-google-card-price-row';
  const GOOGLE_CARD_ACTIVE_ATTRIBUTE = 'data-codex-google-card-active';
  const GOOGLE_CARD_COMPARE_ATTRIBUTE = 'data-codex-google-card-compare';
  const GOOGLE_CARD_COMPARE_TEXT_ATTRIBUTE = 'data-codex-google-card-compare-text';
  const GOOGLE_CARD_SAVE_BADGE_ATTRIBUTE = 'data-codex-google-card-save-badge';
  const GOOGLE_CARD_ORIGINAL_SALE_CLASS_ATTRIBUTE = 'data-codex-google-card-original-sale-class';
  const GOOGLE_CARD_ORIGINAL_SALE_TEXT_ATTRIBUTE = 'data-codex-google-card-original-sale-text';
  const GOOGLE_CARD_ORIGINAL_SALE_LABEL_ATTRIBUTE = 'data-codex-google-card-original-sale-label';
  const COLLECTION_SIDEBAR_SELECTOR = '.shopify-section--main-collection .collection--filters-sidebar, .shopify-section--main-search .collection--filters-sidebar';
  const COLLECTION_ACTIVE_FACETS_SELECTOR = '.collection__active-facets';
  const COLLECTION_SORT_TRIGGER_SELECTOR = '.collection__top-bar facet-sort-by > button[aria-controls]';
  const COLLECTION_FIRST_PRODUCT_SELECTOR = '.collection__results .product-card__title a[href]';
  const COLLECTION_RESULT_FOCUSABLE_SELECTOR = [
    '.collection__results .product-card__title a[href]',
    '.collection__results a[href]',
    '.collection__results button:not([disabled])',
    '.collection__results input:not([disabled])',
    '.collection__results select:not([disabled])',
    '.collection__results textarea:not([disabled])',
    '.collection__results [tabindex]:not([tabindex="-1"])',
  ].join(', ');
  const FACET_UPDATED_EVENT = 'facet:updated';
  const GOOGLE_CARD_OFFER_STORAGE_KEY = 'codexGoogleAutomatedDiscountOffer';
  const GOOGLE_CARD_STORAGE_TTL_SECONDS = 4 * 60 * 60;
  const GOOGLE_CARD_SYNC_DELAYS = [0, 250, 750, 1500];
  const SEARCH_ORIGINAL_QUERY_PARAM = 'ool_original_query';
  const SEARCH_RESULTS_HEADING_SELECTOR = '[data-codex-search-results-heading]';
  const CART_RECOMMENDATIONS_SELECTOR = 'scroll-carousel[data-codex-cart-recommendations]';
  const SEARCH_QUERY_OVERRIDES = new Map([
    ['olives', 'title:"olives"'],
    ['honey', 'title:"honey"'],
    ['novello', '"novello"'],
    ['nuovo', '"novello"'],
    ['novo', '"novello"'],
    ['novus', '"novello"'],
    ['novellos', '"novello"'],
    ['entelia', '"Entelia"'],
    ['desierto', '"Oro del Desierto"'],
    ['oro del desierto', '"Oro del Desierto"'],
    ['oro bailen', '"Oro Bailen"'],
    ['bailen', '"Oro Bailen"'],
    ['frantoi cutrera', '"frantoi cutrera"'],
    ['cutrera', '"frantoi cutrera"'],
    ['accademia', '"accademia olearia"'],
    ['olearia', '"accademia olearia"'],
    ['accademia olearia', '"accademia olearia"'],
    ['almaoliva', 'title:"almaoliva" OR tag:"Almaoliva"'],
    ['almazaras', 'title:"almazaras" OR tag:"producer:Almazaras de la Subbetica"'],
    ['basirico', 'title:"basirico"'],
    ['belvis de las navas', 'tag:"producer:Belvis de las Navas"'],
    ['calivirgin', 'title:"calivirgin"'],
    ['castillo de canena', 'title:"castillo de canena"'],
    ['chateau de montfrin', 'title:"chateau de montfrin"'],
    ['creta verde', 'title:"creta verde"'],
    ['crete gold', 'title:"crete gold"'],
    ['de carlo', 'title:"de carlo"'],
    ['frantoio bonamini', 'title:"frantoio bonamini"'],
    ['bonamini', 'title:"frantoio bonamini"'],
    ['frantoio franci', 'title:"franci"'],
    ['franci', 'title:"franci"'],
    ['frantoio pruneti', 'title:"pruneti"'],
    ['pruneti', 'title:"pruneti"'],
    ['marina colonna', 'title:"colonna"'],
    ['colonna', 'title:"colonna"'],
    ['marina palusci', 'title:"palusci"'],
    ['palusci', 'title:"palusci"'],
    ['mueloliva', 'tag:"producer:Mueloliva"'],
    ['my olive tree', 'title:"my olive tree"'],
    ['olive tree', 'title:"my olive tree"'],
    ['olio guglielmi', 'title:"guglielmi"'],
    ['guglielmi', 'title:"guglielmi"'],
    ['oro di milas', 'title:"oro di milas"'],
    ['palacio de los olivos', 'title:"palacio de los olivos"'],
    ['palacio', 'title:"palacio de los olivos"'],
    ['parqueoliva', 'title:"parqueoliva"'],
    ['rincon de la subbetica', 'title:"rincon de la subbetica"'],
    ['rincon', 'title:"rincon de la subbetica"'],
    ['terre francescane', 'title:"francescane"'],
    ['francescane', 'title:"francescane"'],
    ['gradassi', 'title:"gradassi"'],
  ]);
  const PILL_SCROLL_SHELL_CLASS = 'codex-pills-scroll-shell';
  const PILL_SCROLL_ARROW_CLASS = 'codex-pills-scroll-arrow';
  const PILL_SCROLL_FOCUS_ATTRIBUTE = 'data-codex-pill-scroll-focus';
  const PILL_SCROLL_DESCRIPTION_ID = 'codex-product-pills-scroll-instructions';
  const MOBILE_BREAKPOINT_QUERY = '(max-width: 699px)';
  const MOTION_STATE_STORAGE_KEY = 'codexMotionPaused';
  const MOTION_STATE_EVENT = 'codex:motion-state-change';
  const MOTION_TOGGLE_LABEL_PATTERN = /pause animations|resume animations/i;
  const MOTION_RESET_LABEL_PATTERN = /reset all accessibility settings/i;
  const MOTION_AUTOPLAY_SELECTORS = [
    'announcement-bar',
    'x-slideshow',
    'slideshow-carousel',
    'mega-menu-promo-carousel',
    'press-carousel',
    'multiple-images-with-text-image-list',
    'multiple-images-with-text-content-list',
  ].join(', ');
  const mobileBreakpoint = typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_BREAKPOINT_QUERY) : null;
  const motionReducedBreakpoint = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  let googleCardSyncQueued = false;
  let googleCardObserver = null;
  let motionPausedPreference = false;
  let motionPausedState = false;
  let motionConsumerSyncQueued = false;
  let motionConsumerObserver = null;
  let consentmoBannerFocusQueued = false;
  let consentmoBannerDismissalQueued = false;
  let consentmoDismissalProxy = null;
  let consentmoBannerObserver = null;
  let consentmoBannerFocusHandled = false;
  let consentmoAccessibilityMenuFocusArmed = false;
  let consentmoAccessibilityMenuReturnTrigger = null;
  let consentmoAccessibilityMenuReturnTimer = null;
  let consentmoAccessibilityMenuReturnPending = false;
  let consentmoAccessibilityMenuReturnToSkip = false;
  let consentmoAccessibilityMenuEarlyTabTarget = null;
  let collectionFacetTabReturnState = null;

  function focusElement(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }

    return document.activeElement === element;
  }

  function getSkipToContentHeading(mainContent) {
    if (!(mainContent instanceof HTMLElement)) {
      return null;
    }

    const pageType = document.body?.dataset.pageType || window.themeVariables?.settings?.pageType || '';
    const headingSelector = pageType === HOME_PAGE_TYPE ? HOME_MAIN_HEADING_SELECTOR : MAIN_HEADING_SELECTOR;

    return Array.from(mainContent.querySelectorAll(headingSelector))
      .find((heading) => heading instanceof HTMLElement
        && heading.getClientRects().length > 0
        && !heading.closest('[hidden], [aria-hidden="true"], [inert]')) || null;
  }

  /**
   * Let the native fragment navigation update scroll position and history, then
   * explicitly transfer focus. Every non-homepage route prefers its first
   * rendered H1 so screen readers announce the page identity. Because the
   * homepage H1 is the linked header logo, its equivalent main-content target
   * is the first rendered H2. Every target uses tabindex="-1", so none becomes
   * another stop in the ordinary tab order.
   */
  function handleSkipToContent(event) {
    const skipLink = event.target instanceof Element
      ? event.target.closest(SKIP_TO_CONTENT_SELECTOR)
      : null;

    if (!(skipLink instanceof HTMLAnchorElement)) {
      return;
    }

    const mainContent = document.getElementById('main');

    if (!(mainContent instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();

    window.requestAnimationFrame(() => {
      const preferredHeading = getSkipToContentHeading(mainContent);
      const focusTarget = preferredHeading instanceof HTMLElement ? preferredHeading : mainContent;

      if (focusTarget === preferredHeading) {
        preferredHeading.setAttribute('tabindex', '-1');
      }

      focusTarget.scrollIntoView({ block: 'start' });
      focusElement(focusTarget);
      window.history.replaceState(window.history.state, '', '#main');
    });

    window.setTimeout(() => {
      const preferredHeading = getSkipToContentHeading(mainContent);
      const focusTarget = preferredHeading instanceof HTMLElement ? preferredHeading : mainContent;

      if (focusTarget === preferredHeading) {
        preferredHeading.setAttribute('tabindex', '-1');
      }

      focusElement(focusTarget);
    }, 100);
  }

  function getFooterBypassTarget() {
    const footer = document.querySelector(FOOTER_SELECTOR);

    return footer instanceof HTMLElement ? footer : null;
  }

  function activateFooterBypassTarget({ scroll = true } = {}) {
    const footer = getFooterBypassTarget();

    if (!(footer instanceof HTMLElement)) {
      return false;
    }

    if (!footer.hasAttribute('tabindex')) {
      footer.setAttribute('tabindex', '-1');
    }

    const focusFooter = () => {
      if (scroll) {
        footer.scrollIntoView({ block: 'start' });
      }

      focusElement(footer);
    };

    window.requestAnimationFrame(focusFooter);
    window.setTimeout(focusFooter, 100);

    return true;
  }

  function getSkipToFooterTrigger(target) {
    const trigger = target instanceof Element
      ? target.closest(SKIP_TO_FOOTER_SELECTOR)
      : null;

    if (!(trigger instanceof HTMLElement)) {
      return null;
    }

    if (trigger.id === 'csm-ada-skip-to-footer'
      || trigger.matches('.skip-to-footer[href="#footer"], [data-codex-skip-to-footer]')) {
      return trigger;
    }

    return /skip to footer/i.test(getConsentmoElementValue(trigger)) ? trigger : null;
  }

  function handleSkipToFooter(event) {
    const trigger = getSkipToFooterTrigger(event.target);

    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    const isNativeFooterLink = trigger instanceof HTMLAnchorElement
      && trigger.getAttribute('href') === '#footer';

    if (event.type === 'keydown') {
      const key = event.key;

      if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') {
        return;
      }

      if (isNativeFooterLink) {
        if (key === 'Enter') {
          // Enter already dispatches the anchor's native click activation.
          return;
        }

        // Anchors do not activate with Space by default. Route Space through
        // click so it receives the same native fragment navigation as Enter.
        event.preventDefault();
        event.stopPropagation();
        trigger.click();
        return;
      }
    }

    const useNativeFragmentNavigation = event.type === 'click' && isNativeFooterLink;

    if (!activateFooterBypassTarget({ scroll: !useNativeFragmentNavigation })) {
      return;
    }

    if (useNativeFragmentNavigation) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  function isUnmodifiedSpaceKey(event) {
    return (event.key === ' ' || event.key === 'Spacebar')
      && !event.altKey
      && !event.ctrlKey
      && !event.metaKey
      && !event.shiftKey;
  }

  function isUnmodifiedEnterKey(event) {
    return event.key === 'Enter'
      && !event.altKey
      && !event.ctrlKey
      && !event.metaKey
      && !event.shiftKey;
  }

  /**
   * Native anchors activate with Enter, but Space normally scrolls the page.
   * For the header logo, product titles, and links inside the main navigation
   * disclosure, Space is an intentional keyboard activation shortcut. Keep
   * the scope narrow so ordinary content links retain native behavior.
   */
  function handleScopedSpaceActivation(event) {
    if (event.type !== 'keydown' || event.defaultPrevented || event.repeat || !isUnmodifiedSpaceKey(event)) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest(`${HEADER_LOGO_LINK_SELECTOR}, ${PRODUCT_TITLE_LINK_SELECTOR}, ${NAVIGATION_DROPDOWN_LINK_SELECTOR}`);
    const summary = target?.closest(NAVIGATION_DROPDOWN_SUMMARY_SELECTOR);
    const activationTarget = link instanceof HTMLAnchorElement
      ? link
      : summary instanceof HTMLElement
        ? summary
        : null;

    if (!(activationTarget instanceof HTMLElement) || activationTarget.getAttribute('aria-disabled') === 'true') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    activationTarget.click();
  }

  /**
   * A summary normally receives native Enter/Space activation. Explicitly
   * route Enter as well as Space through the same click path so custom
   * disclosure elements behave consistently across browsers and screen
   * readers. Preventing the key's default action also prevents page scrolling.
   */
  function handleNavigationDisclosureKeyboard(event) {
    if (event.type !== 'keydown' || event.defaultPrevented || event.repeat || (!isUnmodifiedEnterKey(event) && !isUnmodifiedSpaceKey(event))) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const summary = target?.closest(NAVIGATION_DROPDOWN_SUMMARY_SELECTOR);

    if (!(summary instanceof HTMLElement) || summary.getAttribute('aria-disabled') === 'true') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    summary.click();
  }

  function getConsentmoWidgetTrigger() {
    const visibleTrigger = Array.from(document.querySelectorAll(CONSENTMO_WIDGET_TRIGGER_SELECTOR))
      .filter((element) => element instanceof HTMLElement)
      .filter((element) => !element.closest('.codex-bypass-links'))
      .filter((element) => !element.closest('.csm-ada-skip-buttons'))
      .filter(isVisibleElement)
      .find((element) => {
        const value = getConsentmoElementValue(element);

        return /accessibility tools/i.test(value)
          || CONSENTMO_ACCESSIBILITY_PATTERNS.some((pattern) => pattern.test(value));
      });

    if (visibleTrigger instanceof HTMLElement) {
      return visibleTrigger;
    }

    const skipWidgetTrigger = document.querySelector(CONSENTMO_SKIP_TO_WIDGET_SELECTOR);

    return skipWidgetTrigger instanceof HTMLElement ? skipWidgetTrigger : null;
  }

  function handleOpenAccessibilityWidget(event) {
    const trigger = event.target instanceof Element
      ? event.target.closest(OPEN_ACCESSIBILITY_WIDGET_SELECTOR)
      : null;

    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearConsentmoAccessibilityMenuReturn();
    consentmoAccessibilityMenuFocusArmed = true;
    consentmoAccessibilityMenuReturnTrigger = trigger;

    if (!openConsentmoAccessibilityMenu()) {
      consentmoAccessibilityMenuFocusArmed = false;
      consentmoAccessibilityMenuReturnTrigger = null;
    }
  }

  /** Older Consentmo builds expose this keyboard path before the public SDK. */
  function dispatchConsentmoSkipWidgetActivation() {
    const skipWidgetTrigger = document.querySelector(CONSENTMO_SKIP_TO_WIDGET_SELECTOR);

    if (!(skipWidgetTrigger instanceof HTMLElement)) {
      return false;
    }

    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      bubbles: true,
      cancelable: true,
    });

    skipWidgetTrigger.dispatchEvent(enterEvent);

    return enterEvent.defaultPrevented;
  }

  function openConsentmoAccessibilityMenu() {
    const accessibilityApi = window.Consentmo?.accessibility;

    if (accessibilityApi && typeof accessibilityApi.open === 'function') {
      accessibilityApi.open();
      return true;
    }

    if (dispatchConsentmoSkipWidgetActivation()) {
      return true;
    }

    const widgetTrigger = getConsentmoWidgetTrigger();

    if (!(widgetTrigger instanceof HTMLElement) || typeof widgetTrigger.click !== 'function') {
      return false;
    }

    widgetTrigger.click();
    return true;
  }

  function getConsentmoAccessibilityMenuReturnTrigger() {
    if (consentmoAccessibilityMenuReturnTrigger instanceof HTMLElement
      && consentmoAccessibilityMenuReturnTrigger.isConnected) {
      return consentmoAccessibilityMenuReturnTrigger;
    }

    const trigger = document.querySelector(OPEN_ACCESSIBILITY_WIDGET_SELECTOR);

    return trigger instanceof HTMLElement ? trigger : null;
  }

  function focusConsentmoAccessibilityMenuReturnTrigger() {
    const trigger = getConsentmoAccessibilityMenuReturnTrigger();

    return trigger instanceof HTMLElement && focusElement(trigger);
  }

  function getSkipToContentLink() {
    const skipToContent = document.querySelector(SKIP_TO_CONTENT_SELECTOR);

    return skipToContent instanceof HTMLElement ? skipToContent : null;
  }

  function getThemeBypassControls() {
    const bypassLinks = document.querySelector('.codex-bypass-links');

    if (!(bypassLinks instanceof HTMLElement)) {
      return [];
    }

    return Array.from(bypassLinks.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element instanceof HTMLElement);
  }

  function getConsentmoAccessibilityMenuPendingTabTarget(shiftKey) {
    const bypassControls = getThemeBypassControls();

    if (bypassControls.length === 0) {
      return null;
    }

    const currentIndex = bypassControls.indexOf(document.activeElement);

    if (currentIndex >= 0) {
      const offset = shiftKey ? -1 : 1;
      const nextIndex = (currentIndex + offset + bypassControls.length) % bypassControls.length;

      return bypassControls[nextIndex];
    }

    if (shiftKey) {
      const returnTrigger = getConsentmoAccessibilityMenuReturnTrigger();
      const returnIndex = bypassControls.indexOf(returnTrigger);

      if (returnIndex >= 0) {
        return bypassControls[(returnIndex - 1 + bypassControls.length) % bypassControls.length];
      }
    }

    return getSkipToContentLink() || bypassControls[0];
  }

  function clearConsentmoAccessibilityMenuReturn() {
    if (consentmoAccessibilityMenuReturnTimer) {
      window.clearTimeout(consentmoAccessibilityMenuReturnTimer);
      consentmoAccessibilityMenuReturnTimer = null;
    }

    consentmoAccessibilityMenuReturnPending = false;
    consentmoAccessibilityMenuReturnToSkip = false;
    consentmoAccessibilityMenuEarlyTabTarget = null;
  }

  function handleConsentmoAccessibilityMenuClosed() {
    if (!consentmoAccessibilityMenuFocusArmed) {
      return;
    }

    clearConsentmoAccessibilityMenuReturn();
    consentmoAccessibilityMenuReturnPending = true;
    consentmoAccessibilityMenuReturnTimer = window.setTimeout(() => {
      consentmoAccessibilityMenuReturnTimer = null;

      if (consentmoAccessibilityMenuEarlyTabTarget instanceof HTMLElement) {
        const earlyTabTarget = consentmoAccessibilityMenuEarlyTabTarget;

        consentmoAccessibilityMenuReturnPending = false;
        consentmoAccessibilityMenuFocusArmed = false;
        consentmoAccessibilityMenuEarlyTabTarget = null;

        if (earlyTabTarget.isConnected) {
          focusElement(earlyTabTarget);
        }

        return;
      }

      const focusRestored = focusConsentmoAccessibilityMenuReturnTrigger();

      consentmoAccessibilityMenuReturnPending = false;
      consentmoAccessibilityMenuFocusArmed = false;
      consentmoAccessibilityMenuReturnToSkip = focusRestored;
    }, CONSENTMO_ACCESSIBILITY_FOCUS_DELAY_MS);
  }

  function handleConsentmoAccessibilityMenuReturnTab(event) {
    if ((!consentmoAccessibilityMenuReturnPending && !consentmoAccessibilityMenuReturnToSkip)
      || event.key !== 'Tab'
      || event.defaultPrevented
      || event.altKey
      || event.ctrlKey
      || event.metaKey) {
      return;
    }

    if (event.shiftKey && consentmoAccessibilityMenuReturnToSkip) {
      clearConsentmoAccessibilityMenuReturn();
      return;
    }

    if (consentmoAccessibilityMenuReturnPending) {
      const pendingTarget = getConsentmoAccessibilityMenuPendingTabTarget(event.shiftKey);

      if (!(pendingTarget instanceof HTMLElement)) {
        clearConsentmoAccessibilityMenuReturn();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      consentmoAccessibilityMenuEarlyTabTarget = pendingTarget;
      focusElement(pendingTarget);
      return;
    }

    const skipToContent = getSkipToContentLink();

    if (!(skipToContent instanceof HTMLElement)) {
      clearConsentmoAccessibilityMenuReturn();
      return;
    }

    const returnTrigger = getConsentmoAccessibilityMenuReturnTrigger();

    if (!(returnTrigger instanceof HTMLElement) || document.activeElement !== returnTrigger) {
      clearConsentmoAccessibilityMenuReturn();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    clearConsentmoAccessibilityMenuReturn();
    focusElement(skipToContent);
  }

  function handleConsentmoAccessibilityMenuReturnFocusChange(event) {
    if (!consentmoAccessibilityMenuReturnToSkip) {
      return;
    }

    const returnTrigger = getConsentmoAccessibilityMenuReturnTrigger();

    if (event.target !== returnTrigger) {
      clearConsentmoAccessibilityMenuReturn();
    }
  }

  function handleConsentmoAccessibilityMenuReturnPointerDown(event) {
    if (!consentmoAccessibilityMenuReturnPending || !event.isTrusted) {
      return;
    }

    const focusTarget = event.target instanceof Element
      ? event.target.closest('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
      : null;

    if (focusTarget instanceof HTMLElement) {
      // Respect a deliberate pointer choice while still refocusing it after
      // Consentmo's delayed cleanup, which otherwise blurs the new target.
      consentmoAccessibilityMenuEarlyTabTarget = focusTarget;
      return;
    }

    clearConsentmoAccessibilityMenuReturn();
    consentmoAccessibilityMenuFocusArmed = false;
  }

  function getConsentmoElementValue(element) {
    return [
      element.textContent || '',
      element.getAttribute('aria-label') || '',
      element.getAttribute('title') || '',
      element.getAttribute('value') || '',
    ].join(' ').replace(/\s+/g, ' ').trim();
  }

  /**
   * Consentmo injects Cookie Settings as a focusable li[role="button"]. Keep
   * the li as the app-owned event target/list item, but move the action's
   * semantics and focus stop to a native nested button. Clicks still bubble to
   * Consentmo's existing listener on the li.
   */
  function syncConsentmoFooterCookieSettings() {
    document.querySelectorAll(CONSENTMO_FOOTER_COOKIE_ITEM_SELECTOR).forEach((item) => {
      const existingButton = item.querySelector(`:scope > button[${CONSENTMO_FOOTER_COOKIE_BUTTON_ATTRIBUTE}]`);

      if (existingButton instanceof HTMLButtonElement) {
        item.removeAttribute('role');
        item.removeAttribute('tabindex');
        return;
      }

      const visibleLabel = (item.textContent || '').replace(/\s+/g, ' ').trim();
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'csm-cookie-widget-button';
      button.setAttribute(CONSENTMO_FOOTER_COOKIE_BUTTON_ATTRIBUTE, 'true');
      button.textContent = visibleLabel || 'Cookie Settings';

      Array.from(item.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('aria-')) {
          button.setAttribute(attribute.name, attribute.value);
          item.removeAttribute(attribute.name);
        }
      });

      item.removeAttribute('role');
      item.removeAttribute('tabindex');

      button.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && !event.altKey && !event.ctrlKey && !event.metaKey) {
          event.stopPropagation();
        }
      });

      item.replaceChildren(button);
    });
  }

  function getConsentmoCookieBannerHost() {
    return document.querySelector('csm-cookie-consent');
  }

  function getConsentmoCookieBannerRoot() {
    const host = getConsentmoCookieBannerHost();

    if (!(host instanceof HTMLElement) || !host.shadowRoot) {
      return null;
    }

    return host.shadowRoot;
  }

  function isVisibleElement(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const computedStyle = window.getComputedStyle(element);

    return computedStyle.display !== 'none'
      && computedStyle.visibility !== 'hidden'
      && computedStyle.opacity !== '0'
      && element.getClientRects().length > 0;
  }

  function getCollectionFacetTabContext(target) {
    if (!(target instanceof Element) || !window.matchMedia('(min-width: 1000px)').matches) {
      return null;
    }

    const collection = target.closest(COLLECTION_SIDEBAR_SELECTOR);
    const section = collection?.closest('.shopify-section');

    if (!(collection instanceof HTMLElement) || !(section instanceof HTMLElement)) {
      return null;
    }

    const activeFacets = collection.querySelector(COLLECTION_ACTIVE_FACETS_SELECTOR);
    const sortTrigger = collection.querySelector(COLLECTION_SORT_TRIGGER_SELECTOR);

    if (!(activeFacets instanceof HTMLElement) || !(sortTrigger instanceof HTMLButtonElement)) {
      return null;
    }

    const activeControls = Array.from(activeFacets.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(isVisibleElement);
    const firstProductControl = Array.from(collection.querySelectorAll(COLLECTION_FIRST_PRODUCT_SELECTOR))
      .find(isVisibleElement) || null;
    const firstResultControl = firstProductControl || Array.from(collection.querySelectorAll(COLLECTION_RESULT_FOCUSABLE_SELECTOR))
      .find(isVisibleElement) || null;

    if (!activeControls.length || !(firstResultControl instanceof HTMLElement)) {
      return null;
    }

    return {
      sectionId: section.id,
      collection,
      activeControls,
      firstResultControl,
      sortTrigger,
    };
  }

  function handleCollectionFacetTabSequence(event) {
    if (event.key !== 'Tab' || event.defaultPrevented || event.repeat || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const target = event.target instanceof HTMLElement ? event.target : null;
    const context = getCollectionFacetTabContext(target);

    if (!target || !context) {
      return;
    }

    const lastActiveControl = context.activeControls[context.activeControls.length - 1];
    const matchingState = collectionFacetTabReturnState?.sectionId === context.sectionId
      ? collectionFacetTabReturnState
      : null;
    let nextTarget = null;
    let nextPhase = null;

    if (!event.shiftKey && target === lastActiveControl) {
      nextTarget = context.sortTrigger;
      nextPhase = 'sort-after-active-facets';
    } else if (target === context.sortTrigger && matchingState?.phase === 'sort-after-active-facets') {
      nextTarget = event.shiftKey ? lastActiveControl : context.firstResultControl;
    } else if (event.shiftKey && target === context.firstResultControl) {
      nextTarget = context.sortTrigger;
      nextPhase = 'sort-before-active-facets';
    } else if (event.shiftKey && target === context.sortTrigger && matchingState?.phase === 'sort-before-active-facets') {
      nextTarget = lastActiveControl;
    }

    if (!(nextTarget instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    collectionFacetTabReturnState = nextPhase
      ? { sectionId: context.sectionId, phase: nextPhase }
      : null;
    focusElement(nextTarget);
  }

  function handleCollectionFacetTabFocusChange(event) {
    if (!collectionFacetTabReturnState || !(event.target instanceof Element)) {
      return;
    }

    const section = document.getElementById(collectionFacetTabReturnState.sectionId);
    const target = event.target;
    const keepState = target.closest('facet-sort-by') && section?.contains(target);

    if (!keepState) {
      collectionFacetTabReturnState = null;
    }
  }

  function clearCollectionFacetTabState() {
    collectionFacetTabReturnState = null;
  }

  function isConsentmoBannerAction(element) {
    const value = getConsentmoElementValue(element);
    return CONSENTMO_BANNER_ACTION_PATTERNS.some((pattern) => pattern.test(value));
  }

  function getConsentmoBannerFocusTarget() {
    const bannerRoot = getConsentmoCookieBannerRoot();

    if (!bannerRoot) {
      return null;
    }

    return Array.from(bannerRoot.querySelectorAll('button, a, [role="button"], input[type="button"], input[type="submit"]'))
      .filter(isVisibleElement)
      .find(isConsentmoBannerAction) || null;
  }

  function getConsentmoBannerFocusableElements() {
    const bannerRoot = getConsentmoCookieBannerRoot();

    if (!bannerRoot) {
      return [];
    }

    return Array.from(bannerRoot.querySelectorAll(
      'button, a, [role="button"], input[type="button"], input[type="submit"], [tabindex]:not([tabindex="-1"])',
    ))
      .filter(isVisibleElement)
      .filter((element) => {
        if (element instanceof HTMLButtonElement || element instanceof HTMLAnchorElement) {
          return !element.disabled;
        }

        if (element instanceof HTMLInputElement) {
          return !element.disabled;
        }

      return true;
      });
  }

  function getFocusedConsentmoBannerElement() {
    const bannerRoot = getConsentmoCookieBannerRoot();
    const bannerHost = getConsentmoCookieBannerHost();
    const activeElement = document.activeElement;

    if (!bannerRoot || !(bannerHost instanceof HTMLElement)) {
      return activeElement instanceof HTMLElement ? activeElement : null;
    }

    if (bannerRoot.activeElement instanceof HTMLElement) {
      return bannerRoot.activeElement;
    }

    if (activeElement === bannerHost) {
      return activeElement;
    }

    return activeElement instanceof HTMLElement ? activeElement : null;
  }

  function syncConsentmoBannerFocus() {
    if (consentmoBannerFocusHandled) {
      return;
    }

    const bannerRoot = getConsentmoCookieBannerRoot();
    const activeElement = getFocusedConsentmoBannerElement();

    if (!bannerRoot) {
      return;
    }

    if (activeElement instanceof HTMLElement && activeElement !== document.body && activeElement !== document.documentElement) {
      if (!bannerRoot.contains(activeElement) && !activeElement.matches(SKIP_TO_CONTENT_SELECTOR)) {
        return;
      }

      if (bannerRoot.contains(activeElement) && isConsentmoBannerAction(activeElement)) {
        consentmoBannerFocusHandled = true;
        return;
      }
    }

    if (activeElement instanceof HTMLElement && bannerRoot.contains(activeElement) && !isConsentmoBannerAction(activeElement)) {
      // Allow the banner's own first actionable control to replace non-action
      // or announcement-only focus, but do not keep touching focus after that.
    } else if (activeElement instanceof HTMLElement && activeElement !== document.body && activeElement !== document.documentElement && !activeElement.matches(SKIP_TO_CONTENT_SELECTOR)) {
      return;
    }

    const focusTarget = getConsentmoBannerFocusTarget();

    if (!(focusTarget instanceof HTMLElement)) {
      return;
    }

    if (focusElement(focusTarget)) {
      consentmoBannerFocusHandled = true;
    }
  }

  function queueConsentmoBannerFocusSync() {
    if (consentmoBannerFocusQueued) {
      return;
    }

    consentmoBannerFocusQueued = true;

    window.requestAnimationFrame(() => {
      consentmoBannerFocusQueued = false;
      syncConsentmoBannerFocus();
    });
  }

  function queueConsentmoBannerDismissalFocus() {
    if (consentmoBannerDismissalQueued) {
      return;
    }

    consentmoBannerDismissalQueued = true;

    const syncDismissalFocus = () => {
      const bannerRoot = getConsentmoCookieBannerRoot();
      const bannerHost = getConsentmoCookieBannerHost();
      const bannerStillVisible = bannerRoot
        && bannerHost instanceof HTMLElement
        && isVisibleElement(bannerHost)
        && getConsentmoBannerFocusableElements().length > 0;

      if (bannerStillVisible) {
        window.requestAnimationFrame(syncDismissalFocus);
        return;
      }

      consentmoBannerDismissalQueued = false;

      const dismissalProxy = getConsentmoDismissalFocusProxy();

      if (dismissalProxy instanceof HTMLElement) {
        focusElement(dismissalProxy);
      }
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(syncDismissalFocus);
    });
  }

  function cleanupConsentmoDismissalFocusProxy() {
    if (consentmoDismissalProxy instanceof HTMLElement && consentmoDismissalProxy.isConnected) {
      consentmoDismissalProxy.remove();
    }

    consentmoDismissalProxy = null;
  }

  function getConsentmoDismissalFocusProxy() {
    if (consentmoDismissalProxy instanceof HTMLElement && consentmoDismissalProxy.isConnected) {
      return consentmoDismissalProxy;
    }

    if (!(document.body instanceof HTMLElement)) {
      return null;
    }

    const proxy = document.createElement('button');

    proxy.type = 'button';
    proxy.setAttribute('aria-hidden', 'true');
    proxy.setAttribute('tabindex', '0');
    proxy.setAttribute('data-codex-consentmo-dismissal-proxy', 'true');
    proxy.style.cssText = [
      'position: fixed',
      'left: 0',
      'top: 0',
      'width: 1px',
      'height: 1px',
      'margin: 0',
      'padding: 0',
      'border: 0',
      'opacity: 0',
      'pointer-events: none',
    ].join('; ');

    proxy.addEventListener('blur', () => {
      window.requestAnimationFrame(() => {
        cleanupConsentmoDismissalFocusProxy();
      });
    });

    document.body.prepend(proxy);
    consentmoDismissalProxy = proxy;

    return proxy;
  }

  function handleConsentmoBannerKeydown(event) {
    if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const bannerRoot = getConsentmoCookieBannerRoot();

    if (!bannerRoot) {
      return;
    }

    const activeElement = getFocusedConsentmoBannerElement();
    const focusables = getConsentmoBannerFocusableElements();

    if (!focusables.length) {
      return;
    }

    const activeIndex = focusables.findIndex((element) => element === activeElement);

    if (activeIndex === -1) {
      event.preventDefault();
      focusElement(event.shiftKey ? focusables[focusables.length - 1] : focusables[0]);
      return;
    }

    event.preventDefault();

    const nextIndex = event.shiftKey
      ? (activeIndex - 1 + focusables.length) % focusables.length
      : (activeIndex + 1) % focusables.length;

    focusElement(focusables[nextIndex]);
  }

  function handleConsentmoBannerActionClick(event) {
    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const actionElement = eventPath.find((node) => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      return node.matches('button, a, [role="button"], input[type="button"], input[type="submit"]')
        && isConsentmoBannerAction(node);
    });

    if (!(actionElement instanceof HTMLElement)) {
      return;
    }

    const bannerRoot = getConsentmoCookieBannerRoot();

    if (!bannerRoot || !bannerRoot.contains(actionElement)) {
      return;
    }

    queueConsentmoBannerDismissalFocus();
  }

  function observeConsentmoBannerFocus() {
    if (consentmoBannerObserver || !(document.body instanceof HTMLElement)) {
      return;
    }

    consentmoBannerObserver = new MutationObserver(() => {
      queueConsentmoBannerFocusSync();
      syncConsentmoFooterCookieSettings();
    });
    consentmoBannerObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Consentmo's accessibility shortcuts focus the page landmarks directly.
   * Shopify creates the footer element as a section wrapper, so its tabindex
   * cannot be declared in footer.liquid and is supplied here instead.
   */
  function syncBypassTargets() {
    document.querySelectorAll(FOOTER_SELECTOR).forEach((footer) => {
      if (!footer.hasAttribute('tabindex')) {
        footer.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * Consentmo's own bypass cluster is retained by the app for compatibility,
   * but the theme-owned cluster is the single user-facing bypass mechanism.
   * Hide the app-owned group from both the accessibility tree and sequential
   * keyboard navigation so dynamic app injection cannot duplicate the controls.
   */
  function syncConsentmoBypassGroups() {
    const canonicalGroup = document.querySelector(CANONICAL_BYPASS_SELECTOR);

    document.querySelectorAll('.codex-bypass-links').forEach((group) => {
      if (group === canonicalGroup) {
        return;
      }

      hideDuplicateBypassGroup(group);
    });

    document.querySelectorAll(CONSENTMO_BYPASS_SELECTOR).forEach((group) => {
      hideDuplicateBypassGroup(group);
    });

    document.querySelectorAll('nav, [role="navigation"], [role="group"], [role="region"]').forEach((group) => {
      if (!(group instanceof HTMLElement) || group.closest('.codex-bypass-links') || group.hidden) {
        return;
      }

      const label = group.getAttribute('aria-label') || '';
      const text = group.textContent.replace(/\s+/g, ' ').trim();
      const isDuplicateBypassGroup = /bypass links/i.test(label)
        || (/skip to content/i.test(text) && (/skip to footer/i.test(text) || /open accessibility/i.test(text)));

      if (isDuplicateBypassGroup) {
        hideDuplicateBypassGroup(group);
      }
    });
  }

  function hideDuplicateBypassGroup(group) {
    if (!(group instanceof HTMLElement)) {
      return;
    }

    if (group.getAttribute('aria-hidden') !== 'true') {
      group.setAttribute('aria-hidden', 'true');
    }

    if (!group.hasAttribute('inert')) {
      group.setAttribute('inert', '');
    }

    if (!group.hidden) {
      group.hidden = true;
    }

    if (group.style.display !== 'none') {
      group.style.setProperty('display', 'none', 'important');
    }
  }

  let consentmoBypassObserver = null;

  function observeConsentmoBypassGroups() {
    if (consentmoBypassObserver || !(document.body instanceof HTMLElement)) {
      return;
    }

    consentmoBypassObserver = new MutationObserver(() => {
      syncConsentmoBypassGroups();
    });
    consentmoBypassObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'class', 'id', 'style'],
    });
  }

  /**
   * Return the canonical full-page query for a configured exact search term.
   *
   * This intentionally does not run while the customer types. The predictive
   * search component therefore continues to send the original query to
   * Shopify and keeps Search & Discovery synonym behavior intact.
   */
  function getFullSearchQueryOverride(query) {
    const normalizedQuery = query.trim().toLocaleLowerCase('en-US');

    return SEARCH_QUERY_OVERRIDES.get(normalizedQuery) || '';
  }

  function isStorefrontSearchForm(form) {
    if (!(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') {
      return false;
    }

    const actionUrl = new URL(form.action, window.location.origin);
    const normalizedPath = actionUrl.pathname.replace(/\/+$/, '');
    const rootPath = window.Shopify?.routes?.root || '/';
    const searchPath = `${rootPath.replace(/\/+$/, '')}/search`.replace(/\/+/g, '/');

    return actionUrl.origin === window.location.origin && normalizedPath === searchPath;
  }

  /**
   * Rewrite only an actual full-page form submission. Predictive requests are
   * fetches made before submit and never pass through this handler.
   *
   * Build the destination URL in memory instead of changing the visible search
   * input. This prevents the canonical query from flashing in the field while
   * the browser navigates. The original query is carried in the URL so the
   * results page can restore customer-facing copy without another request.
   */
  function handleSearchQueryOverride(event) {
    const form = event.target;

    if (!isStorefrontSearchForm(form)) {
      return;
    }

    const queryInput = form.elements.namedItem('q');

    if (!(queryInput instanceof HTMLInputElement)) {
      return;
    }

    const originalQuery = queryInput.value.trim();
    const canonicalQuery = getFullSearchQueryOverride(originalQuery);

    if (!canonicalQuery) {
      return;
    }

    const formData = event.submitter
      ? new FormData(form, event.submitter)
      : new FormData(form);
    const destinationUrl = new URL(form.action, window.location.origin);

    formData.set('q', canonicalQuery);
    formData.set(SEARCH_ORIGINAL_QUERY_PARAM, originalQuery);
    destinationUrl.search = new URLSearchParams(formData).toString();

    event.preventDefault();
    window.location.assign(destinationUrl.toString());
  }

  /**
   * Restore the query the customer typed after Shopify renders results for the
   * mapped query. Replacing the complete mapped search syntax preserves the
   * locale's surrounding results-count punctuation.
   */
  function restoreOriginalSearchQuery() {
    const currentUrl = new URL(window.location.href);
    const originalQuery = currentUrl.searchParams.get(SEARCH_ORIGINAL_QUERY_PARAM)?.trim() || '';
    const canonicalQuery = currentUrl.searchParams.get('q') || '';

    if (!originalQuery || getFullSearchQueryOverride(originalQuery) !== canonicalQuery) {
      return;
    }

    document.querySelectorAll('.shopify-section--main-search .main-search-form input[name="q"]').forEach((queryInput) => {
      if (queryInput instanceof HTMLInputElement) {
        queryInput.value = originalQuery;
      }
    });

    document.querySelectorAll(SEARCH_RESULTS_HEADING_SELECTOR).forEach((heading) => {
      if (heading.textContent.includes(canonicalQuery)) {
        heading.textContent = heading.textContent.replace(canonicalQuery, originalQuery);
      }
    });

    document.querySelectorAll('.shopify-section--main-search form[is="facet-form"]').forEach((facetForm) => {
      if (!(facetForm instanceof HTMLFormElement)) {
        return;
      }

      let originalQueryInput = facetForm.elements.namedItem(SEARCH_ORIGINAL_QUERY_PARAM);

      if (!(originalQueryInput instanceof HTMLInputElement)) {
        originalQueryInput = document.createElement('input');
        originalQueryInput.type = 'hidden';
        originalQueryInput.name = SEARCH_ORIGINAL_QUERY_PARAM;
        facetForm.append(originalQueryInput);
      }

      originalQueryInput.value = originalQuery;
    });
  }

  function readStoredMotionPausedPreference() {
    try {
      return window.localStorage.getItem(MOTION_STATE_STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  function writeStoredMotionPausedPreference(paused) {
    try {
      window.localStorage.setItem(MOTION_STATE_STORAGE_KEY, paused ? 'true' : 'false');
    } catch (error) {
      /* Ignore storage failures. */
    }
  }

  function applyGlobalMotionPaused(paused) {
    const pausedState = Boolean(paused);

    motionPausedPreference = pausedState;

    if (typeof window.__OOLSetMotionPaused === 'function') {
      window.__OOLSetMotionPaused(pausedState);
    } else {
      writeStoredMotionPausedPreference(pausedState);
    }

    syncMotionState('consentmo');
    queueMotionConsumerSync();
  }

  function isSystemReducedMotion() {
    return !!motionReducedBreakpoint?.matches;
  }

  function getEffectiveMotionPaused() {
    return isSystemReducedMotion() || motionPausedPreference;
  }

  function emitMotionStateChange(source) {
    document.dispatchEvent(new CustomEvent(MOTION_STATE_EVENT, {
      detail: {
        paused: motionPausedState,
        source,
        systemReducedMotion: isSystemReducedMotion(),
        preferencePaused: motionPausedPreference,
      },
    }));
  }

  function syncMotionState(source = 'init') {
    const nextState = getEffectiveMotionPaused();
    const previousState = motionPausedState;

    motionPausedState = nextState;
    window.__OOLMotionPaused = nextState;

    if (document.documentElement instanceof HTMLElement) {
      document.documentElement.toggleAttribute('data-codex-motion-paused', nextState);
      document.documentElement.classList.toggle('codex-motion-paused', nextState);
    }

    if (previousState !== nextState) {
      emitMotionStateChange(source);
    }
  }

  function syncMotionConsumers() {
    document.querySelectorAll(MOTION_AUTOPLAY_SELECTORS).forEach((element) => {
      const player = element.player;

      if (!player) {
        return;
      }

      if (motionPausedState) {
        player.__codexMotionPausedByBridge = true;
        player.pause();
        return;
      }

      if (player.__codexMotionPausedByBridge && element.hasAttribute('autoplay')) {
        player.__codexMotionPausedByBridge = false;
        player.resume(true);
      }
    });
  }

  function queueMotionConsumerSync() {
    if (motionConsumerSyncQueued) {
      return;
    }

    motionConsumerSyncQueued = true;

    window.requestAnimationFrame(() => {
      motionConsumerSyncQueued = false;
      syncMotionConsumers();
    });
  }

  function ensureMotionConsumerObserver() {
    if (motionConsumerObserver || !(document.body instanceof HTMLElement)) {
      return;
    }

    motionConsumerObserver = new MutationObserver(() => {
      if (motionPausedState) {
        queueMotionConsumerSync();
      }
    });
    motionConsumerObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function patchAutoplayPlayer(player) {
    if (!(player instanceof EventTarget) || player.__codexMotionBridgePatched) {
      return;
    }

    const originalPause = typeof player.pause === 'function' ? player.pause.bind(player) : null;
    const originalResume = typeof player.resume === 'function' ? player.resume.bind(player) : null;
    const originalStart = typeof player.start === 'function' ? player.start.bind(player) : null;

    if (!originalPause || !originalResume || !originalStart) {
      return;
    }

    player.pause = () => originalPause();
    player.resume = (restartTimer = false) => {
      if (motionPausedState) {
        originalPause();
        return;
      }

      return originalResume(restartTimer);
    };
    player.start = () => {
      if (motionPausedState) {
        originalPause();
        return;
      }

      return originalStart();
    };
    player.__codexMotionBridgePatched = true;
  }

  function syncMotionCarousel(element) {
    const player = element?.player;

    if (!player) {
      return;
    }

    patchAutoplayPlayer(player);

    if (motionPausedState) {
      player.__codexMotionPausedByBridge = true;
      player.pause();
      return;
    }

    if (player.__codexMotionPausedByBridge && element.hasAttribute('autoplay')) {
      player.__codexMotionPausedByBridge = false;
      player.resume(true);
    }
  }

  function patchEffectCarouselMotion() {
    const EffectCarousel = window.customElements?.get('effect-carousel');

    if (!EffectCarousel || EffectCarousel.prototype.__codexMotionBridgePatched) {
      return;
    }

    const originalConnectedCallback = EffectCarousel.prototype.connectedCallback;
    const originalDisconnectedCallback = EffectCarousel.prototype.disconnectedCallback;

    EffectCarousel.prototype.connectedCallback = function(...args) {
      const result = typeof originalConnectedCallback === 'function'
        ? originalConnectedCallback.apply(this, args)
        : undefined;

      syncMotionCarousel(this);

      if (!this.__codexMotionStateListener) {
        this.__codexMotionStateListener = () => {
          syncMotionCarousel(this);
        };
        document.addEventListener(MOTION_STATE_EVENT, this.__codexMotionStateListener);
      }

      return result;
    };

    EffectCarousel.prototype.disconnectedCallback = function(...args) {
      if (this.__codexMotionStateListener) {
        document.removeEventListener(MOTION_STATE_EVENT, this.__codexMotionStateListener);
        this.__codexMotionStateListener = null;
      }

      if (typeof originalDisconnectedCallback === 'function') {
        return originalDisconnectedCallback.apply(this, args);
      }
    };

    EffectCarousel.prototype.__codexMotionBridgePatched = true;
  }

  function getMotionToggleControl(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    const control = target.closest('button, [role="button"], [aria-pressed], [aria-checked], [data-action], [data-testid], [data-state], [data-active], [data-selected]');

    if (!(control instanceof HTMLElement)) {
      return null;
    }

    const label = [
      control.getAttribute('aria-label'),
      control.getAttribute('title'),
      control.textContent,
      control.value,
    ].filter(Boolean).join(' ').trim();

    if (!MOTION_TOGGLE_LABEL_PATTERN.test(label) && !MOTION_RESET_LABEL_PATTERN.test(label)) {
      return null;
    }

    return control;
  }

  function getMotionToggleControlFromEvent(event) {
    if (!event) {
      return null;
    }

    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    for (const node of path) {
      if (!(node instanceof Element)) {
        continue;
      }

      const control = getMotionToggleControl(node);

      if (control) {
        return control;
      }
    }

    return getMotionToggleControl(event.target);
  }

  function inferMotionPausedFromControl(control) {
    if (!(control instanceof HTMLElement)) {
      return null;
    }

    if (MOTION_RESET_LABEL_PATTERN.test([
      control.getAttribute('aria-label'),
      control.getAttribute('title'),
      control.textContent,
      control.value,
    ].filter(Boolean).join(' '))) {
      return false;
    }

    const ariaPressed = control.getAttribute('aria-pressed');
    if (ariaPressed === 'true') {
      return true;
    }
    if (ariaPressed === 'false') {
      return false;
    }

    const ariaChecked = control.getAttribute('aria-checked');
    if (ariaChecked === 'true') {
      return true;
    }
    if (ariaChecked === 'false') {
      return false;
    }

    const datasetState = [
      control.dataset.state,
      control.dataset.active,
      control.dataset.selected,
    ].find(Boolean);

    if (datasetState) {
      const normalizedState = datasetState.toLowerCase();

      if (['active', 'selected', 'on', 'true'].includes(normalizedState)) {
        return true;
      }

      if (['inactive', 'off', 'false', 'disabled'].includes(normalizedState)) {
        return false;
      }
    }

    if (control.classList.contains('active') || control.classList.contains('is-active') || control.classList.contains('selected') || control.classList.contains('is-selected')) {
      return true;
    }

    if (control.classList.contains('inactive') || control.classList.contains('is-inactive') || control.classList.contains('off')) {
      return false;
    }

    return null;
  }

  function setMotionPausedPreference(paused, source = 'consentmo') {
    motionPausedPreference = Boolean(paused);

    if (typeof window.__OOLSetMotionPaused === 'function') {
      window.__OOLSetMotionPaused(motionPausedPreference);
    } else {
      writeStoredMotionPausedPreference(motionPausedPreference);
    }

    syncMotionState(source);
    queueMotionConsumerSync();
  }

  function handleMotionToggleClick(event) {
    const control = getMotionToggleControlFromEvent(event);

    if (!control) {
      return;
    }

    window.setTimeout(() => {
      const inferredPaused = inferMotionPausedFromControl(control);

      if (inferredPaused === null) {
        setMotionPausedPreference(!motionPausedPreference, 'consentmo-toggle');
        return;
      }

      setMotionPausedPreference(inferredPaused, 'consentmo-toggle');
    }, 0);
  }

  function getSignalPausedPreference(detail, seen = new WeakSet()) {
    if (!detail || typeof detail !== 'object' || seen.has(detail)) {
      return null;
    }

    seen.add(detail);

    const entries = Array.isArray(detail) ? detail.entries() : Object.entries(detail);

    for (const [key, value] of entries) {
      const normalizedKey = String(key).toLowerCase();

      if (value && typeof value === 'object') {
        const nestedPreference = getSignalPausedPreference(value, seen);

        if (nestedPreference !== null) {
          return nestedPreference;
        }
      }

      if (!/(pause|animat|motion|reduce)/.test(normalizedKey)) {
        continue;
      }

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (['true', 'paused', 'pause', 'on', 'enabled', 'reduce', 'reduced'].includes(normalizedValue)) {
          return true;
        }

        if (['false', 'resumed', 'resume', 'play', 'off', 'disabled', 'normal'].includes(normalizedValue)) {
          return false;
        }
      }

      if (typeof value === 'number') {
        if (value === 1) {
          return true;
        }

        if (value === 0) {
          return false;
        }
      }
    }

    return null;
  }

  function handleConsentmoSignal(event) {
    const inferredPaused = getSignalPausedPreference(event?.detail);

    if (inferredPaused === null) {
      return;
    }

    applyGlobalMotionPaused(inferredPaused);
  }

  function installMotionBridge() {
    motionPausedPreference = readStoredMotionPausedPreference();
    syncMotionState('init');
    queueMotionConsumerSync();
    ensureMotionConsumerObserver();

    if (typeof window.customElements?.whenDefined === 'function') {
      window.customElements.whenDefined('effect-carousel').then(patchEffectCarouselMotion);
    } else {
      patchEffectCarouselMotion();
    }

    if (motionReducedBreakpoint) {
      const handleReducedMotionChange = () => {
        syncMotionState('system');
        queueMotionConsumerSync();
      };

      if (typeof motionReducedBreakpoint.addEventListener === 'function') {
        motionReducedBreakpoint.addEventListener('change', handleReducedMotionChange);
      } else if (typeof motionReducedBreakpoint.addListener === 'function') {
        motionReducedBreakpoint.addListener(handleReducedMotionChange);
      }
    }

    document.addEventListener(MOTION_STATE_EVENT, queueMotionConsumerSync);
    document.addEventListener('click', handleMotionToggleClick, true);
    document.addEventListener('click', handleConsentmoBannerActionClick, true);
    document.addEventListener('consentmoSignal_onLoad', handleConsentmoSignal);
    document.addEventListener('consentmoSignal', handleConsentmoSignal);
    document.addEventListener('keydown', handleConsentmoBannerKeydown, true);
  }

  function installConsentmoBannerFocus() {
    queueConsentmoBannerFocusSync();
    observeConsentmoBannerFocus();

    document.addEventListener('consentmoSignal_onLoad', queueConsentmoBannerFocusSync);
    document.addEventListener('consentmoSignal', queueConsentmoBannerFocusSync);
  }

  function getMainSections(mainElement) {
    return Array.from(mainElement.children).filter((child) => {
      return child instanceof HTMLElement && child.classList.contains('shopify-section');
    });
  }

  function isCodexHiddenSection(sectionElement) {
    return sectionElement.querySelector(HIDDEN_SECTION_SELECTOR) !== null;
  }

  function normalizeHash(hashValue) {
    const normalizedHash = hashValue.trim();
    return normalizedHash === '' ? '-1' : normalizedHash;
  }

  function isMobileViewport() {
    return mobileBreakpoint ? mobileBreakpoint.matches : window.innerWidth <= 699;
  }

  function getMobileClassTokens(classValue) {
    return classValue
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean);
  }

  function syncMobileOnlyClasses() {
    const isMobile = isMobileViewport();

    document.querySelectorAll(MOBILE_ONLY_CLASS_SELECTOR).forEach((container) => {
      if (!(container instanceof HTMLElement)) {
        return;
      }

      const mobileClassTokens = getMobileClassTokens(container.dataset.codexMobileClass || '');
      const desktopClassTokens = getMobileClassTokens(container.dataset.codexDesktopClass || '');

      if (mobileClassTokens.length === 0) {
        return;
      }

      if (!container.hasAttribute(MOBILE_CLASS_BASE_ATTRIBUTE)) {
        container.setAttribute(MOBILE_CLASS_BASE_ATTRIBUTE, Array.from(container.classList).join(' '));
      }

      const baseClassSet = new Set(getMobileClassTokens(container.getAttribute(MOBILE_CLASS_BASE_ATTRIBUTE) || ''));
      const desktopClassSet = new Set(desktopClassTokens);
      const mobileClassSet = new Set(mobileClassTokens);

      if (isMobile) {
        desktopClassTokens.forEach((classToken) => {
          if (!mobileClassSet.has(classToken)) {
            container.classList.remove(classToken);
          }
        });

        mobileClassTokens.forEach((classToken) => {
          container.classList.add(classToken);
        });

        return;
      }

      desktopClassTokens.forEach((classToken) => {
        container.classList.add(classToken);
      });

      mobileClassTokens.forEach((classToken) => {
        if (!desktopClassSet.has(classToken) && !baseClassSet.has(classToken)) {
          container.classList.remove(classToken);
        }
      });
    });
  }

  function createPillArrowButton(direction) {
    const button = document.createElement('button');
    const isPrev = direction === 'prev';

    button.className = `${PILL_SCROLL_ARROW_CLASS} ${PILL_SCROLL_ARROW_CLASS}--${direction}`;
    button.type = 'button';
    button.setAttribute('aria-label', isPrev ? 'Scroll left' : 'Scroll right');
    button.innerHTML = isPrev ? '&#8249;' : '&#8250;';

    return button;
  }

  function setupLargeOneColPillShell(pillRow) {
    if (!(pillRow instanceof HTMLElement)) {
      return null;
    }

    if (pillRow.dataset.codexPillShellReady === 'true') {
      const existingShell = pillRow.closest(`.${PILL_SCROLL_SHELL_CLASS}`);

      if (existingShell instanceof HTMLElement) {
        return existingShell;
      }

      delete pillRow.dataset.codexPillShellReady;
    }

    const parent = pillRow.parentElement;

    if (!(parent instanceof HTMLElement)) {
      return null;
    }

    const shell = document.createElement('div');
    const previousButton = createPillArrowButton('prev');
    const nextButton = createPillArrowButton('next');
    const epsilon = 2;

    shell.className = PILL_SCROLL_SHELL_CLASS;

    parent.insertBefore(shell, pillRow);
    shell.append(previousButton, pillRow, nextButton);

    const getScrollStep = () => {
      return Math.max(Math.round(pillRow.clientWidth * 0.8), 120);
    };

    const updateArrowState = () => {
      const maxScrollLeft = Math.max(pillRow.scrollWidth - pillRow.clientWidth, 0);
      const currentScrollLeft = Math.max(pillRow.scrollLeft, 0);
      const atStart = currentScrollLeft <= epsilon;
      const atEnd = currentScrollLeft >= maxScrollLeft - epsilon;

      previousButton.disabled = atStart;
      nextButton.disabled = atEnd;
    };

    previousButton.addEventListener('click', () => {
      pillRow.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    });

    nextButton.addEventListener('click', () => {
      pillRow.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    });

    const onPillRowScroll = () => {
      window.requestAnimationFrame(updateArrowState);
    };

    pillRow.addEventListener('scroll', onPillRowScroll, { passive: true });

    pillRow.dataset.codexPillShellReady = 'true';
    pillRow.codexUpdatePillArrowState = updateArrowState;
    pillRow.codexPillArrowScrollHandler = onPillRowScroll;

    updateArrowState();

    return shell;
  }

  function teardownLargeOneColPillShell(shell) {
    if (!(shell instanceof HTMLElement)) {
      return;
    }

    const pillRow = shell.querySelector('.product-properties__pills');
    const parent = shell.parentElement;

    if (!(pillRow instanceof HTMLElement) || !(parent instanceof HTMLElement)) {
      shell.remove();
      return;
    }

    if (typeof pillRow.codexPillArrowScrollHandler === 'function') {
      pillRow.removeEventListener('scroll', pillRow.codexPillArrowScrollHandler);
    }

    delete pillRow.codexPillArrowScrollHandler;
    delete pillRow.codexUpdatePillArrowState;
    delete pillRow.dataset.codexPillShellReady;

    parent.insertBefore(pillRow, shell);
    shell.remove();
  }

  function syncLargeOneColPillNavigation() {
    const isMobile = isMobileViewport();
    const activeLargeRows = new Set(document.querySelectorAll(LARGE_ONE_COL_PILLS_SELECTOR));

    document.querySelectorAll(`.${PILL_SCROLL_SHELL_CLASS}`).forEach((shell) => {
      if (!(shell instanceof HTMLElement)) {
        return;
      }

      const pillRow = shell.querySelector('.product-properties__pills');
      const shouldKeepShell = isMobile && pillRow instanceof HTMLElement && activeLargeRows.has(pillRow);

      if (!shouldKeepShell) {
        teardownLargeOneColPillShell(shell);
      }
    });

    if (!isMobile) {
      return;
    }

    activeLargeRows.forEach((pillRow) => {
      const shell = setupLargeOneColPillShell(pillRow);

      if (!(shell instanceof HTMLElement) || !(pillRow instanceof HTMLElement)) {
        return;
      }

      const hasHorizontalOverflow = pillRow.scrollWidth > pillRow.clientWidth + 1;
      const showArrows = isMobile && hasHorizontalOverflow;

      shell.classList.toggle('is-active', showArrows);

      if (typeof pillRow.codexUpdatePillArrowState === 'function') {
        pillRow.codexUpdatePillArrowState();
      }
    });
  }

  function getPillGroupLabel(pillRow) {
    const group = pillRow.closest('.product-properties__group');
    const label = group?.querySelector('.product-info__attribute-label');

    if (!(label instanceof HTMLElement)) {
      return null;
    }

    if (!label.id) {
      const section = pillRow.closest('.shopify-section');
      const sectionKey = (section?.id || 'product-properties').replace(/[^a-zA-Z0-9_-]/g, '-');
      const labelIndex = Array.from(document.querySelectorAll('.product-info__attribute-label')).indexOf(label) + 1;
      label.id = `codex-product-properties-label-${sectionKey}-${labelIndex}`;
    }

    if (!/^H[1-6]$/.test(label.tagName)) {
      label.setAttribute('role', 'heading');
      label.setAttribute('aria-level', '2');
    }

    return label;
  }

  function ensurePillScrollDescription() {
    let description = document.getElementById(PILL_SCROLL_DESCRIPTION_ID);

    if (!(description instanceof HTMLElement)) {
      const mainContent = document.querySelector(MAIN_SELECTOR);

      if (!(mainContent instanceof HTMLElement)) {
        return null;
      }

      description = document.createElement('span');
      description.id = PILL_SCROLL_DESCRIPTION_ID;
      description.className = 'sr-only';
      description.textContent = 'Use the Left and Right Arrow keys to scroll through items.';
      mainContent.append(description);
    }

    return description;
  }

  function normalizeProductPillSemantics(pillRow) {
    const label = getPillGroupLabel(pillRow);

    if (label && !pillRow.hasAttribute('aria-labelledby')) {
      pillRow.setAttribute('aria-labelledby', label.id);
    }

    if (!pillRow.matches('ul, ol') && !pillRow.hasAttribute('role')) {
      pillRow.setAttribute('role', 'list');
    }

    Array.from(pillRow.children).forEach((pill) => {
      if (!(pill instanceof HTMLElement) || !pill.classList.contains('pill')) {
        return;
      }

      if (!pillRow.matches('ul, ol') && !pill.hasAttribute('role')) {
        pill.setAttribute('role', 'listitem');
      }

      const icon = pill.querySelector(':scope > img');
      const visibleLabel = pill.querySelector(':scope > span');

      if (icon instanceof HTMLImageElement && visibleLabel instanceof HTMLElement) {
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function syncProductPillScrollSemantics() {
    const isMobile = isMobileViewport();
    const rows = Array.from(document.querySelectorAll(PRODUCT_PILLS_SELECTOR));

    rows.forEach((pillRow) => {
      if (!(pillRow instanceof HTMLElement)) {
        return;
      }

      normalizeProductPillSemantics(pillRow);

      if (pillRow.dataset.codexPillOriginalAttributesCaptured !== 'true') {
        pillRow.dataset.codexPillOriginalAttributesCaptured = 'true';
        pillRow.dataset.codexPillOriginalTabindex = pillRow.getAttribute('tabindex') || '';
        pillRow.dataset.codexPillOriginalDescribedby = pillRow.getAttribute('aria-describedby') || '';
      }

      const styles = window.getComputedStyle(pillRow);
      const hasHorizontalOverflow = pillRow.scrollWidth > pillRow.clientWidth + 1;
      const isKeyboardScrollable = isMobile && hasHorizontalOverflow && ['auto', 'scroll'].includes(styles.overflowX);

      if (isKeyboardScrollable) {
        pillRow.setAttribute('tabindex', '0');
        pillRow.setAttribute(PILL_SCROLL_FOCUS_ATTRIBUTE, 'true');

        const description = ensurePillScrollDescription();

        if (!(description instanceof HTMLElement)) {
          return;
        }

        const describedBy = new Set((pillRow.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        describedBy.add(description.id);
        pillRow.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
        return;
      }

      if (pillRow.getAttribute(PILL_SCROLL_FOCUS_ATTRIBUTE) !== 'true') {
        return;
      }

      const originalTabindex = pillRow.dataset.codexPillOriginalTabindex || '';
      const originalDescribedby = pillRow.dataset.codexPillOriginalDescribedby || '';

      if (originalTabindex) {
        pillRow.setAttribute('tabindex', originalTabindex);
      } else {
        pillRow.removeAttribute('tabindex');
      }

      if (originalDescribedby) {
        pillRow.setAttribute('aria-describedby', originalDescribedby);
      } else {
        pillRow.removeAttribute('aria-describedby');
      }

      pillRow.removeAttribute(PILL_SCROLL_FOCUS_ATTRIBUTE);
    });
  }

  function handleProductPillScrollKeydown(event) {
    const pillRow = event.target instanceof HTMLElement
      && event.target.getAttribute(PILL_SCROLL_FOCUS_ATTRIBUTE) === 'true'
      ? event.target
      : null;

    if (!(pillRow instanceof HTMLElement)) {
      return;
    }

    const maxScrollLeft = Math.max(pillRow.scrollWidth - pillRow.clientWidth, 0);
    const scrollStep = Math.max(Math.round(pillRow.clientWidth * 0.8), 120);
    let nextScrollLeft = null;

    switch (event.key) {
      case 'ArrowLeft':
        nextScrollLeft = Math.max(pillRow.scrollLeft - scrollStep, 0);
        break;
      case 'ArrowRight':
        nextScrollLeft = Math.min(pillRow.scrollLeft + scrollStep, maxScrollLeft);
        break;
      case 'Home':
        nextScrollLeft = 0;
        break;
      case 'End':
        nextScrollLeft = maxScrollLeft;
        break;
      default:
        return;
    }

    event.preventDefault();
    pillRow.scrollTo({ left: nextScrollLeft, behavior: 'auto' });
  }

  function syncProductAwardsSemantics() {
    document.querySelectorAll(PRODUCT_AWARDS_SELECTOR).forEach((awardsSection) => {
      if (!(awardsSection instanceof HTMLElement)) {
        return;
      }

      const heading = awardsSection.querySelector('.awards-headline');
      const awardsList = awardsSection.querySelector('.awards-right');
      const divider = awardsSection.querySelector('.awards-divider');

      if (heading instanceof HTMLElement) {
        if (!heading.id) {
          const section = awardsSection.closest('.shopify-section');
          const sectionKey = (section?.id || 'product-awards').replace(/[^a-zA-Z0-9_-]/g, '-');
          heading.id = `codex-product-awards-heading-${sectionKey}`;
        }

        if (!/^H[1-6]$/.test(heading.tagName)) {
          heading.setAttribute('role', 'heading');
          heading.setAttribute('aria-level', '2');
        }
      }

      if (awardsList instanceof HTMLElement) {
        if (!awardsList.matches('ul, ol') && !awardsList.hasAttribute('role')) {
          awardsList.setAttribute('role', 'list');
        }

        if (heading instanceof HTMLElement && !awardsList.hasAttribute('aria-labelledby')) {
          awardsList.setAttribute('aria-labelledby', heading.id);
        }

        Array.from(awardsList.children).forEach((award) => {
          if (!(award instanceof HTMLElement) || !award.classList.contains('award-badge')) {
            return;
          }

          if (!awardsList.matches('ul, ol') && !award.hasAttribute('role')) {
            award.setAttribute('role', 'listitem');
          }

          award.querySelectorAll('.badge-org-line2').forEach((line) => {
            if (line instanceof HTMLElement && line.textContent.trim() === '') {
              line.setAttribute('aria-hidden', 'true');
            }
          });
        });
      }

      divider?.setAttribute('aria-hidden', 'true');
    });
  }

  function syncConditionalSectionSpacing(root = document) {
    const mainElements = root instanceof Element && root.matches(MAIN_SELECTOR) ? [root] : Array.from(document.querySelectorAll(MAIN_SELECTOR));

    mainElements.forEach((mainElement) => {
      let previousVisibleHash = '-1';
      let firstVisibleSection = true;

      getMainSections(mainElement).forEach((sectionElement) => {
        if (isCodexHiddenSection(sectionElement)) {
          sectionElement.dataset.codexHiddenApplied = 'true';
          sectionElement.hidden = true;
          sectionElement.style.display = 'none';
          return;
        }

        if (sectionElement.dataset.codexHiddenApplied === 'true') {
          delete sectionElement.dataset.codexHiddenApplied;
          sectionElement.hidden = false;
          sectionElement.style.removeProperty('display');
        }

        sectionElement.style.setProperty('--previous-section-background-hash', previousVisibleHash);
        sectionElement.style.setProperty('--section-is-first', firstVisibleSection ? '1' : '0');

        previousVisibleHash = normalizeHash(getComputedStyle(sectionElement).getPropertyValue('--section-background-hash'));
        firstVisibleSection = false;
      });
    });
  }

  function isOolMegaMenuOpen(disclosure) {
    return disclosure.open || disclosure.hasAttribute('open');
  }

  function forceCloseOolMegaMenu(disclosure) {
    if (!(disclosure instanceof HTMLDetailsElement)) {
      return;
    }

    if (disclosure._hoverTimer) {
      window.clearTimeout(disclosure._hoverTimer);
      disclosure._hoverTimer = null;
    }

    disclosure.open = false;
    disclosure.removeAttribute('open');

    if ('_open' in disclosure) {
      disclosure._open = false;
    }
  }

  function closeOpenOolMegaMenus(target = null) {
    if (target !== null && !(target instanceof Node)) {
      return;
    }

    document.querySelectorAll(OOL_MEGA_MENU_SELECTOR).forEach((menu) => {
      const disclosure = menu.closest('details[is="mega-menu-disclosure"]');

      if (!(disclosure instanceof HTMLDetailsElement) || !isOolMegaMenuOpen(disclosure)) {
        return;
      }

      if (target !== null && disclosure.contains(target)) {
        return;
      }

      forceCloseOolMegaMenu(disclosure);
    });
  }

  function handleOolMegaMenuOutsideClick(event) {
    closeOpenOolMegaMenus(event.target);
  }

  function handleOolMegaMenuEscape(event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') {
      return;
    }

    const activeDisclosure = event.target instanceof Element
      ? event.target.closest('details[is="mega-menu-disclosure"]')
      : null;
    const shouldRestoreFocus = activeDisclosure instanceof HTMLDetailsElement
      && isOolMegaMenuOpen(activeDisclosure)
      && activeDisclosure.querySelector(OOL_MEGA_MENU_SELECTOR);

    closeOpenOolMegaMenus();

    if (shouldRestoreFocus && activeDisclosure.firstElementChild instanceof HTMLElement) {
      focusElement(activeDisclosure.firstElementChild);
    }
  }

  function handleOolMegaMenuContainerPointerExit(event) {
    if ('pointerType' in event && event.pointerType !== 'mouse') {
      return;
    }

    const pointerX = Number(event.clientX);

    if (!Number.isFinite(pointerX)) {
      return;
    }

    document.querySelectorAll(OOL_MEGA_MENU_SELECTOR).forEach((menu) => {
      const disclosure = menu.closest('details[is="mega-menu-disclosure"]');

      if (!(menu instanceof HTMLElement) || !(disclosure instanceof HTMLDetailsElement) || !isOolMegaMenuOpen(disclosure)) {
        return;
      }

      const panel = menu.querySelector(OOL_MEGA_MENU_PANEL_SELECTOR);
      const boundsElement = panel instanceof HTMLElement ? panel : menu;
      const bounds = boundsElement.getBoundingClientRect();

      if (pointerX < bounds.left || pointerX > bounds.right) {
        forceCloseOolMegaMenu(disclosure);
      }
    });
  }

  function parseMoney(value) {
    if (typeof value !== 'string') {
      return NaN;
    }

    const match = value.replace(/\s+/g, ' ').match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) {
      return NaN;
    }

    return Number.parseFloat(match[0].replace(/,/g, ''));
  }

  function getDecimalPlaces(value) {
    const decimalPart = String(value).split('.')[1];

    return decimalPart ? decimalPart.length : 0;
  }

  function formatMoneyFromSample(amount, sampleText) {
    const fallback = `$${amount.toFixed(2)}`;

    if (!Number.isFinite(amount) || typeof sampleText !== 'string' || sampleText.trim() === '') {
      return fallback;
    }

    const match = sampleText.match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) {
      return fallback;
    }

    const sampleNumber = match[0];
    const decimalPlaces = getDecimalPlaces(sampleNumber);
    const hasThousandsSeparator = sampleNumber.includes(',');
    let formattedAmount = amount.toFixed(decimalPlaces);

    if (hasThousandsSeparator) {
      const amountParts = formattedAmount.split('.');
      amountParts[0] = amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedAmount = amountParts.join('.');
    }

    return `${sampleText.slice(0, match.index)}${formattedAmount}${sampleText.slice(match.index + sampleNumber.length)}`;
  }

  function decodeBase64Url(value) {
    const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalizedValue.length % 4)) % 4);

    return window.atob(`${normalizedValue}${padding}`);
  }

  function getPv2Token() {
    const currentParams = new URLSearchParams(window.location.search);

    if (currentParams.has('pv2')) {
      return currentParams.get('pv2') || '';
    }

    const shopifyPageUrl = window.__st && typeof window.__st.pageurl === 'string' ? window.__st.pageurl : '';
    const queryStart = shopifyPageUrl.indexOf('?');

    if (queryStart === -1) {
      return '';
    }

    return new URLSearchParams(shopifyPageUrl.slice(queryStart)).get('pv2') || '';
  }

  function getOfferIds(offerId) {
    const idMatches = String(offerId || '').match(/\d{6,}/g) || [];

    return {
      productId: idMatches.length > 1 ? idMatches[idMatches.length - 2] : '',
      variantId: idMatches.length > 0 ? idMatches[idMatches.length - 1] : '',
    };
  }

  function normalizeGoogleCardOffer(offer) {
    if (!offer || !Number.isFinite(Number(offer.price)) || String(offer.variantId || '') === '') {
      return null;
    }

    const normalizedOffer = {
      price: Number(offer.price),
      productId: String(offer.productId || ''),
      variantId: String(offer.variantId || ''),
      expiresAt: Number.isFinite(Number(offer.expiresAt)) ? Number(offer.expiresAt) : null,
      savedAt: Number.isFinite(Number(offer.savedAt)) ? Number(offer.savedAt) : Date.now() / 1000,
    };

    if (!isGoogleCardOfferFresh(normalizedOffer)) {
      return null;
    }

    return normalizedOffer;
  }

  function isGoogleCardOfferFresh(offer) {
    const nowSeconds = Date.now() / 1000;

    if (!offer || !Number.isFinite(Number(offer.price)) || String(offer.variantId || '') === '') {
      return false;
    }

    if (Number.isFinite(Number(offer.expiresAt)) && nowSeconds > Number(offer.expiresAt)) {
      return false;
    }

    if (!Number.isFinite(Number(offer.expiresAt)) && Number.isFinite(Number(offer.savedAt)) && nowSeconds - Number(offer.savedAt) > GOOGLE_CARD_STORAGE_TTL_SECONDS) {
      return false;
    }

    return true;
  }

  function decodePv2GoogleCardOffer() {
    const token = getPv2Token();
    const tokenParts = token.split('.');

    if (tokenParts.length < 2 || typeof window.atob !== 'function') {
      return null;
    }

    try {
      const offerRecord = JSON.parse(decodeBase64Url(tokenParts[1]));
      const offerIds = getOfferIds(offerRecord && offerRecord.o);

      return normalizeGoogleCardOffer({
        price: Number(offerRecord ? offerRecord.p : NaN),
        productId: offerIds.productId,
        variantId: offerIds.variantId,
        expiresAt: Number(offerRecord ? offerRecord.exp : NaN),
      });
    } catch (error) {
      return null;
    }
  }

  function getGoogleCardGadToken() {
    const globalData = window.automatedDiscountsLiquidData;
    const cartAttributes = globalData && typeof globalData === 'object' ? globalData.cartAttributes : null;

    return cartAttributes && typeof cartAttributes._gad === 'string' ? cartAttributes._gad : '';
  }

  function decodeGoogleCardGadOffer(encodedOffer) {
    if (encodedOffer === '' || typeof window.atob !== 'function') {
      return null;
    }

    let decodedOfferText = '';

    try {
      decodedOfferText = window.atob(encodedOffer);
    } catch (error) {
      try {
        decodedOfferText = decodeBase64Url(encodedOffer);
      } catch (innerError) {
        return null;
      }
    }

    try {
      const decodedOffer = JSON.parse(decodedOfferText);
      const offerRecord = decodedOffer && decodedOffer.R ? decodedOffer.R : decodedOffer;
      const offerIds = getOfferIds(offerRecord && (offerRecord.offerId || offerRecord.o));

      return normalizeGoogleCardOffer({
        price: Number(offerRecord ? offerRecord.price ?? offerRecord.p : NaN),
        productId: offerIds.productId,
        variantId: offerIds.variantId,
        expiresAt: Number(offerRecord ? offerRecord.expirationTime ?? offerRecord.exp : NaN),
      });
    } catch (error) {
      return null;
    }
  }

  function readStoredGoogleCardOffer() {
    const storage = getGoogleCardSessionStorage();

    if (!storage) {
      return null;
    }

    try {
      const offer = normalizeGoogleCardOffer(JSON.parse(storage.getItem(GOOGLE_CARD_OFFER_STORAGE_KEY) || 'null'));

      if (!offer) {
        storage.removeItem(GOOGLE_CARD_OFFER_STORAGE_KEY);
      }

      return offer;
    } catch (error) {
      storage.removeItem(GOOGLE_CARD_OFFER_STORAGE_KEY);
      return null;
    }
  }

  function storeGoogleCardOffer(offer) {
    const storage = getGoogleCardSessionStorage();

    if (!storage || !offer) {
      return;
    }

    try {
      storage.setItem(GOOGLE_CARD_OFFER_STORAGE_KEY, JSON.stringify({
        price: offer.price,
        productId: offer.productId,
        variantId: offer.variantId,
        expiresAt: offer.expiresAt,
        savedAt: Date.now() / 1000,
      }));
    } catch (error) {
      // Session storage can be unavailable in privacy modes; the visible PDP/search behavior still works without it.
    }
  }

  function getActiveGoogleCardOffer() {
    const pv2Offer = decodePv2GoogleCardOffer();

    if (pv2Offer) {
      storeGoogleCardOffer(pv2Offer);
      return pv2Offer;
    }

    const gadOffer = decodeGoogleCardGadOffer(getGoogleCardGadToken());

    if (gadOffer) {
      storeGoogleCardOffer(gadOffer);
      return gadOffer;
    }

    return readStoredGoogleCardOffer();
  }

  function getGoogleCardSessionStorage() {
    try {
      return window.sessionStorage || null;
    } catch (error) {
      return null;
    }
  }

  function getGoogleCardPriceLists(root = document) {
    if (root instanceof Element && root.matches(GOOGLE_CARD_PRICE_LIST_SELECTOR)) {
      return [root];
    }

    const queryRoot = root instanceof Element || root instanceof Document ? root : document;

    return Array.from(queryRoot.querySelectorAll(GOOGLE_CARD_PRICE_LIST_SELECTOR));
  }

  function setPriceElementText(priceElement, label, priceText) {
    let screenReaderLabel = priceElement.querySelector('.sr-only');

    if (
      screenReaderLabel instanceof HTMLElement &&
      screenReaderLabel.parentElement === priceElement &&
      screenReaderLabel.textContent === label &&
      priceElement.childNodes.length === 2 &&
      priceElement.childNodes[1].nodeType === Node.TEXT_NODE &&
      priceElement.childNodes[1].textContent === priceText
    ) {
      return;
    }

    screenReaderLabel = document.createElement('span');
    screenReaderLabel.className = 'sr-only';
    screenReaderLabel.textContent = label;

    priceElement.replaceChildren(screenReaderLabel, document.createTextNode(priceText));
  }

  function ensureGoogleCardPriceRow(priceList) {
    let row = priceList.closest(`[${GOOGLE_CARD_PRICE_ROW_ATTRIBUTE}]`);

    if (row instanceof HTMLElement) {
      return row;
    }

    row = document.createElement('span');
    row.className = 'codex-google-card-price-row';
    row.setAttribute(GOOGLE_CARD_PRICE_ROW_ATTRIBUTE, 'true');

    priceList.insertAdjacentElement('beforebegin', row);
    row.append(priceList);

    return row;
  }

  function restoreGoogleCardPriceRow(priceList) {
    const row = priceList.closest(`[${GOOGLE_CARD_PRICE_ROW_ATTRIBUTE}]`);

    if (!(row instanceof HTMLElement) || !(row.parentElement instanceof HTMLElement)) {
      return;
    }

    row.insertAdjacentElement('beforebegin', priceList);
    row.remove();
  }

  function restoreGoogleCardSalePrice(salePrice, priceList) {
    const originalClass = salePrice.getAttribute(GOOGLE_CARD_ORIGINAL_SALE_CLASS_ATTRIBUTE);
    const originalText = salePrice.getAttribute(GOOGLE_CARD_ORIGINAL_SALE_TEXT_ATTRIBUTE);
    const originalLabel = salePrice.getAttribute(GOOGLE_CARD_ORIGINAL_SALE_LABEL_ATTRIBUTE)
      || priceList?.dataset.codexPriceLabel
      || 'Price';

    if (originalClass !== null) {
      salePrice.setAttribute('class', originalClass);
      salePrice.removeAttribute(GOOGLE_CARD_ORIGINAL_SALE_CLASS_ATTRIBUTE);
    }

    if (originalText !== null) {
      setPriceElementText(salePrice, originalLabel, originalText);
      salePrice.removeAttribute(GOOGLE_CARD_ORIGINAL_SALE_TEXT_ATTRIBUTE);
    }

    salePrice.removeAttribute(GOOGLE_CARD_ORIGINAL_SALE_LABEL_ATTRIBUTE);
  }

  function cleanupGoogleCardDiscount(priceList) {
    if (!(priceList instanceof HTMLElement)) {
      return;
    }

    const salePrice = priceList.querySelector('sale-price');

    priceList.removeAttribute(GOOGLE_CARD_ACTIVE_ATTRIBUTE);
    priceList.querySelectorAll(`[${GOOGLE_CARD_COMPARE_ATTRIBUTE}]`).forEach((compareAtPrice) => {
      compareAtPrice.remove();
    });

    if (salePrice instanceof HTMLElement) {
      restoreGoogleCardSalePrice(salePrice, priceList);
    }

    const row = priceList.closest(`[${GOOGLE_CARD_PRICE_ROW_ATTRIBUTE}]`);

    if (row instanceof HTMLElement) {
      row.querySelectorAll(`[${GOOGLE_CARD_SAVE_BADGE_ATTRIBUTE}]`).forEach((badge) => {
        badge.remove();
      });
      restoreGoogleCardPriceRow(priceList);
    }
  }

  function syncGoogleCardSalePrice(salePrice, salePriceText, priceList) {
    if (!salePrice.hasAttribute(GOOGLE_CARD_ORIGINAL_SALE_CLASS_ATTRIBUTE)) {
      salePrice.setAttribute(GOOGLE_CARD_ORIGINAL_SALE_CLASS_ATTRIBUTE, salePrice.getAttribute('class') || '');
    }

    if (!salePrice.hasAttribute(GOOGLE_CARD_ORIGINAL_SALE_TEXT_ATTRIBUTE)) {
      salePrice.setAttribute(GOOGLE_CARD_ORIGINAL_SALE_TEXT_ATTRIBUTE, salePrice.textContent.replace(/\s+/g, ' ').trim());
    }

    if (!salePrice.hasAttribute(GOOGLE_CARD_ORIGINAL_SALE_LABEL_ATTRIBUTE)) {
      const originalLabel = salePrice.querySelector('.sr-only')?.textContent.replace(/\s+/g, ' ').trim()
        || priceList?.dataset.codexPriceLabel
        || 'Price';
      salePrice.setAttribute(GOOGLE_CARD_ORIGINAL_SALE_LABEL_ATTRIBUTE, originalLabel);
    }

    salePrice.classList.add('text-on-sale');
    salePrice.classList.remove('text-subdued');
    setPriceElementText(salePrice, 'Sale price', salePriceText);
  }

  function syncGoogleCardCompareAtPrice(priceList, salePrice, compareAtPriceText) {
    let compareAtPrice = priceList.querySelector(`[${GOOGLE_CARD_COMPARE_ATTRIBUTE}]`);

    if (!(compareAtPrice instanceof HTMLElement)) {
      compareAtPrice = document.createElement('span');
      compareAtPrice.className = 'codex-google-card-compare text-subdued line-through';
      compareAtPrice.setAttribute(GOOGLE_CARD_COMPARE_ATTRIBUTE, 'true');
      salePrice.insertAdjacentElement('afterend', compareAtPrice);
    }

    compareAtPrice.setAttribute(GOOGLE_CARD_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);
    compareAtPrice.setAttribute('aria-label', `Regular price ${compareAtPriceText}`);
    setPriceElementText(compareAtPrice, 'Regular price', compareAtPriceText);

    if (compareAtPrice.previousElementSibling !== salePrice) {
      salePrice.insertAdjacentElement('afterend', compareAtPrice);
    }
  }

  function syncGoogleCardSaveBadge(priceList, savingsText) {
    const row = ensureGoogleCardPriceRow(priceList);
    let saveBadge = row.querySelector(`[${GOOGLE_CARD_SAVE_BADGE_ATTRIBUTE}]`);
    const saveBadgeText = `Save ${savingsText}`;

    if (!(saveBadge instanceof HTMLElement)) {
      saveBadge = document.createElement('on-sale-badge');
      saveBadge.className = 'badge badge--on-sale codex-google-card-save-badge';
      saveBadge.setAttribute(GOOGLE_CARD_SAVE_BADGE_ATTRIBUTE, 'true');
      priceList.insertAdjacentElement('afterend', saveBadge);
    }

    if (saveBadge.textContent.trim() !== saveBadgeText) {
      saveBadge.textContent = saveBadgeText;
    }
  }

  function syncGoogleCardDiscount(priceList, offer) {
    if (!(priceList instanceof HTMLElement)) {
      return;
    }

    if (priceList.dataset.codexGoogleCardNativeCompareAt === 'true' || priceList.dataset.codexGoogleCardPriceVaries === 'true') {
      cleanupGoogleCardDiscount(priceList);
      return;
    }

    if (String(priceList.dataset.codexGoogleCardVariantId || '') !== offer.variantId) {
      cleanupGoogleCardDiscount(priceList);
      return;
    }

    if (offer.productId && priceList.dataset.codexGoogleCardProductId && String(priceList.dataset.codexGoogleCardProductId) !== offer.productId) {
      cleanupGoogleCardDiscount(priceList);
      return;
    }

    const salePrice = priceList.querySelector('sale-price');
    const originalPrice = Number(priceList.dataset.codexGoogleCardPriceCents) / 100;
    const discountPrice = Number(offer.price);
    const moneySample = priceList.dataset.codexGoogleCardPriceText || (salePrice ? salePrice.textContent || '' : '$0.00');
    const savingsAmount = originalPrice - discountPrice;

    if (!(salePrice instanceof HTMLElement) || !Number.isFinite(originalPrice) || !Number.isFinite(discountPrice) || savingsAmount <= 0.005) {
      cleanupGoogleCardDiscount(priceList);
      return;
    }

    const salePriceText = formatMoneyFromSample(discountPrice, moneySample);
    const compareAtPriceText = priceList.dataset.codexGoogleCardPriceText || formatMoneyFromSample(originalPrice, moneySample);
    const savingsText = formatMoneyFromSample(savingsAmount, moneySample);

    syncGoogleCardSalePrice(salePrice, salePriceText, priceList);
    syncGoogleCardCompareAtPrice(priceList, salePrice, compareAtPriceText);
    syncGoogleCardSaveBadge(priceList, savingsText);
    priceList.setAttribute(GOOGLE_CARD_ACTIVE_ATTRIBUTE, 'true');
  }

  function syncGoogleCardDiscounts(root = document) {
    const offer = getActiveGoogleCardOffer();

    if (!offer) {
      return false;
    }

    getGoogleCardPriceLists(root).forEach((priceList) => {
      syncGoogleCardDiscount(priceList, offer);
    });

    return true;
  }

  function queueGoogleCardDiscountSync(event) {
    const root = event?.target instanceof Element ? event.target : document;

    if (!getActiveGoogleCardOffer()) {
      return;
    }

    if (googleCardSyncQueued) {
      return;
    }

    googleCardSyncQueued = true;

    window.requestAnimationFrame(() => {
      googleCardSyncQueued = false;

      if (syncGoogleCardDiscounts(root)) {
        observeGoogleCardDiscountSurfaces();
      }
    });
  }

  function observeGoogleCardDiscountSurfaces() {
    if (googleCardObserver || !(document.body instanceof HTMLElement)) {
      return;
    }

    googleCardObserver = new MutationObserver(() => {
      queueGoogleCardDiscountSync();
    });
    googleCardObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function scheduleGoogleCardDiscountSyncs(event) {
    if (!getActiveGoogleCardOffer()) {
      return;
    }

    GOOGLE_CARD_SYNC_DELAYS.forEach((delay) => {
      window.setTimeout(() => {
        queueGoogleCardDiscountSync(event);
      }, delay);
    });
  }

  function getCartRecommendationSlides(carousel) {
    return Array.from(carousel.querySelectorAll('.horizontal-product'));
  }

  function getCartRecommendationTitle(slide) {
    return slide.querySelector('.horizontal-product__info a[href], .horizontal-product__info .text-sm.bold')?.textContent.replace(/\s+/g, ' ').trim() || 'Product';
  }

  function prepareCartRecommendationSlides(carousel, slides) {
    slides.forEach((slide, index) => {
      if (!slide.id) {
        slide.id = `${carousel.id || 'cart-drawer-recommendations'}-item-${index + 1}`;
      }

      const addButton = slide.querySelector('.horizontal-product__cta button:not([disabled])');
      slide.setAttribute('role', 'group');
      slide.setAttribute('tabindex', '-1');
      slide.setAttribute('aria-label', `Item ${index + 1} of ${slides.length}: ${getCartRecommendationTitle(slide)}${addButton ? '. Quick add to cart available.' : ''}`);
    });
  }

  function getCartRecommendationIndex(carousel, slides) {
    const selectedSlide = slides.find((slide) => slide.classList.contains('is-selected'));
    const selectedIndex = selectedSlide ? slides.indexOf(selectedSlide) : Number(carousel.dataset.codexCartRecommendationIndex);
    const carouselIndex = Number.isInteger(carousel.selectedIndex) ? carousel.selectedIndex : selectedIndex;
    const index = Number.isInteger(carouselIndex) && carouselIndex >= 0 ? carouselIndex : selectedIndex;

    return Math.min(Math.max(index || 0, 0), Math.max(slides.length - 1, 0));
  }

  function announceCartRecommendation(message) {
    const status = document.getElementById('status-announcement');

    if (!(status instanceof HTMLElement)) {
      return;
    }

    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 100);
  }

  function announceSelectedCartRecommendation(carousel, index, includeInstructions = false, announce = true) {
    const slides = getCartRecommendationSlides(carousel);
    const slide = slides[index];

    if (!(slide instanceof HTMLElement)) {
      return;
    }

    prepareCartRecommendationSlides(carousel, slides);
    const title = getCartRecommendationTitle(slide);
    const addButton = slide.querySelector('.horizontal-product__cta button:not([disabled])');
    const instructions = includeInstructions ? ' Use the Left and Right Arrow keys to browse recommendations. Press Enter to open the selected product. Press Space to add it to your cart.' : '';

    carousel.dataset.codexCartRecommendationIndex = String(index);
    carousel.setAttribute('aria-label', includeInstructions ? `Product recommendations. Item ${index + 1} of ${slides.length}: ${title}.${addButton ? ' Quick add to cart available.' : ''}${instructions}` : 'Product recommendations');
    if (announce) {
      announceCartRecommendation(`Product recommendations. Item ${index + 1} of ${slides.length}: ${title}.${addButton ? ' Quick add to cart available.' : ''}${instructions}`);
    }
  }

  function handleCartRecommendationFocus(event) {
    const carousel = event.target instanceof Element ? event.target.closest(CART_RECOMMENDATIONS_SELECTOR) : null;

    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    const focusedSlide = event.target instanceof HTMLElement ? event.target.closest('.horizontal-product') : null;
    if (event.target !== carousel && focusedSlide && carousel.contains(focusedSlide)) {
      carousel.removeAttribute('aria-describedby');
      const slides = getCartRecommendationSlides(carousel);
      prepareCartRecommendationSlides(carousel, slides);
      const index = slides.indexOf(focusedSlide);
      if (index >= 0) {
        carousel.dataset.codexCartRecommendationIndex = String(index);
      }
      return;
    }

    if (event.target !== carousel) {
      return;
    }

    const slides = getCartRecommendationSlides(carousel);
    announceSelectedCartRecommendation(carousel, getCartRecommendationIndex(carousel, slides), true, false);
  }

  function handleCartRecommendationBlur(event) {
    const carousel = event.target instanceof Element ? event.target.closest(CART_RECOMMENDATIONS_SELECTOR) : null;

    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    if (event.relatedTarget instanceof Node && carousel.contains(event.relatedTarget)) {
      return;
    }

    const instructionsId = carousel.getAttribute('data-codex-cart-recommendations-instructions');
    const instructions = instructionsId ? document.getElementById(instructionsId) : null;
    carousel.removeAttribute('aria-describedby');
    carousel.setAttribute('aria-label', 'Product recommendations');
    instructions?.setAttribute('aria-hidden', 'true');
  }

  function handleCartRecommendationKeyboard(event) {
    const carousel = event.target instanceof Element ? event.target.closest(CART_RECOMMENDATIONS_SELECTOR) : null;

    if (!(carousel instanceof HTMLElement) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    const slides = getCartRecommendationSlides(carousel);
    if (slides.length === 0) {
      return;
    }

    prepareCartRecommendationSlides(carousel, slides);
    const focusedSlide = event.target instanceof HTMLElement ? event.target.closest('.horizontal-product') : null;
    if (event.target !== carousel && !(focusedSlide && carousel.contains(focusedSlide))) {
      return;
    }
    const currentIndex = focusedSlide && slides.includes(focusedSlide) ? slides.indexOf(focusedSlide) : getCartRecommendationIndex(carousel, slides);
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      const nextIndex = Math.min(Math.max(currentIndex + (event.key === 'ArrowRight' ? 1 : -1), 0), slides.length - 1);
      carousel.dataset.codexCartRecommendationIndex = String(nextIndex);

      if (typeof carousel.select === 'function') {
        carousel.select(nextIndex, { exact: true });
      } else {
        slides[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }

      const nextSlide = slides[nextIndex];
      window.requestAnimationFrame(() => {
        if (nextSlide instanceof HTMLElement) {
          nextSlide.focus({ preventScroll: true });
        }
      });
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const selectedSlide = slides[currentIndex];
    const target = event.key === 'Enter'
      ? selectedSlide.querySelector('a[href]:not(.horizontal-product__media)')
      : selectedSlide.querySelector('.horizontal-product__cta button:not([disabled])') || selectedSlide.querySelector('a[href]:not(.horizontal-product__media)');

    if (target instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      target.click();
    }
  }

  function handleCartRecommendationButtonClick(event) {
    const button = event.target instanceof Element ? event.target.closest('button[aria-controls="cart-drawer-recommendations"]') : null;

    if (!(button instanceof HTMLElement)) {
      return;
    }

    const carousel = document.getElementById(button.getAttribute('aria-controls'));
    if (!(carousel instanceof HTMLElement)) {
      return;
    }

    window.setTimeout(() => {
      const slides = getCartRecommendationSlides(carousel);
      announceSelectedCartRecommendation(carousel, getCartRecommendationIndex(carousel, slides));
    }, 350);
  }

  function queueCodexGlobalSync(event) {
    const root = event?.target instanceof Element ? event.target : document;

    window.requestAnimationFrame(() => {
      syncBypassTargets();
      syncConsentmoBypassGroups();
      observeConsentmoBypassGroups();
      syncConsentmoFooterCookieSettings();
      queueConsentmoBannerFocusSync();
      observeConsentmoBannerFocus();
      syncMobileOnlyClasses();
      syncLargeOneColPillNavigation();
      syncProductPillScrollSemantics();
      syncProductAwardsSemantics();
      syncConditionalSectionSpacing(root);
      restoreOriginalSearchQuery();
      queueMotionConsumerSync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueCodexGlobalSync, { once: true });
    document.addEventListener('DOMContentLoaded', scheduleGoogleCardDiscountSyncs, { once: true });
    document.addEventListener('DOMContentLoaded', installMotionBridge, { once: true });
    document.addEventListener('DOMContentLoaded', installConsentmoBannerFocus, { once: true });
  } else {
    queueCodexGlobalSync();
    scheduleGoogleCardDiscountSyncs();
    installMotionBridge();
    installConsentmoBannerFocus();
  }

  document.addEventListener('shopify:section:load', queueCodexGlobalSync);
  document.addEventListener('shopify:section:load', scheduleGoogleCardDiscountSyncs);
  document.addEventListener('shopify:section:load', queueMotionConsumerSync);
  document.addEventListener('shopify:section:unload', queueCodexGlobalSync);
  document.addEventListener('shopify:section:reorder', queueCodexGlobalSync);
  document.addEventListener(FACET_UPDATED_EVENT, queueCodexGlobalSync);
  document.addEventListener('click', handleSkipToContent, true);
  document.addEventListener('click', handleSkipToFooter, true);
  document.addEventListener('click', handleOpenAccessibilityWidget, true);
  document.addEventListener('click', handleCartRecommendationButtonClick, true);
  document.addEventListener('click', handleOolMegaMenuOutsideClick, true);
  document.addEventListener('submit', handleSearchQueryOverride, true);
  document.addEventListener('keydown', handleScopedSpaceActivation, true);
  document.addEventListener('keydown', handleProductPillScrollKeydown, true);
  document.addEventListener('keydown', handleCollectionFacetTabSequence, true);
  document.addEventListener('keydown', handleNavigationDisclosureKeyboard, true);
  document.addEventListener('keydown', handleCartRecommendationKeyboard, true);
  document.addEventListener('keydown', handleSkipToFooter, true);
  document.addEventListener('keydown', handleConsentmoAccessibilityMenuReturnTab, true);
  document.addEventListener('keydown', handleOolMegaMenuEscape, true);
  document.addEventListener('focusin', handleConsentmoAccessibilityMenuReturnFocusChange, true);
  document.addEventListener('focusin', handleCartRecommendationFocus, true);
  document.addEventListener('focusout', handleCartRecommendationBlur, true);
  document.addEventListener('focusin', handleCollectionFacetTabFocusChange, true);
  document.addEventListener('pointerdown', handleConsentmoAccessibilityMenuReturnPointerDown, true);
  document.addEventListener('pointerdown', clearCollectionFacetTabState, true);
  document.addEventListener(CONSENTMO_ACCESSIBILITY_CLOSED_EVENT, handleConsentmoAccessibilityMenuClosed);
  document.addEventListener('pointermove', handleOolMegaMenuContainerPointerExit, { passive: true });
  window.addEventListener('resize', queueCodexGlobalSync, { passive: true });
  window.addEventListener('orientationchange', queueCodexGlobalSync);

  if (mobileBreakpoint) {
    if (typeof mobileBreakpoint.addEventListener === 'function') {
      mobileBreakpoint.addEventListener('change', queueCodexGlobalSync);
    } else if (typeof mobileBreakpoint.addListener === 'function') {
      mobileBreakpoint.addListener(queueCodexGlobalSync);
    }
  }
})();
